import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axiosInstance from '../utils/axiosConfig';
import { formatMongoDate, parseMongoDate } from '../utils/dateUtils.js';
import { normalizeUser, normalizeScheduleData } from '../utils/userUtils';
import "../styles/theme.css";
import "../styles/ViewAdminSchedules.css";
import "../styles/ViewSchedulesModern.css";
import ExportButton from './ExportButton';
import { exportData, exportRenderedTableData } from '../utils/exportUtils';
import Select from 'react-select';

import LoadingSpinner from './LoadingSpinner';
import AvailabilityMatrixTable from './AvailabilityMatrixTable';
import { useToast } from '../utils/toastContext';

// Off Badge Component for empty slots
const OffBadge = () => (
    <span style={{
        display: 'inline-block',
        padding: '6px 14px',
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
        color: '#475569',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '600',
        border: '1px solid #cbd5e1',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        letterSpacing: '0.5px',
        textTransform: 'uppercase'
    }}>
        🚫 Off
    </span>
);

// Format date for display
const formatDateForDisplay = (dateString) => {
    try {
        const formattedDate = formatMongoDate(dateString);
        return formattedDate !== 'Invalid Date' ? formattedDate : 'Invalid date';
    } catch (err) {
        console.error("Date formatting error:", err, "for date:", dateString);
        return 'Invalid date';
    }
};
// Format dates for API calls
const formatDateForAPI = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

// Parse dates from various formats
const parseDate = (dateString) => {
    if (!dateString) return null;
    
    let date;
    if (typeof dateString === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            const [year, month, day] = dateString.split('-').map(Number);
            date = new Date(year, month - 1, day);
        } else if (dateString.includes('/')) {
            const [day, month, year] = dateString.split('/');
            date = new Date(year, month - 1, day);
        } else {
            date = new Date(dateString);
        }
    } else {
        date = new Date(dateString);
    }
    
    return isNaN(date.getTime()) ? null : date;
};

const toDisplayText = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.map(toDisplayText).filter(Boolean).join(', ');
    if (typeof value === 'object') {
        return (
            value.name ||
            value.speciality ||
            value.firstname ||
            value.lastname ||
            value.email ||
            value._id ||
            ''
        );
    }
    return '';
};

const getDisplayNameFromUser = (user) => {
    const first = user?.firstname ? String(user.firstname).trim() : '';
    const last = user?.lastname ? String(user.lastname).trim() : '';
    const full = `${first} ${last}`.trim();
    return full || toDisplayText(user?.name) || '';
};

const getRotationStudentId = (student) =>
    toDisplayText(student?.studentId || student?._id || student?.id || '').trim();

const getRotationPreceptorId = (preceptor) =>
    toDisplayText(preceptor?.preceptorId || preceptor?._id || preceptor?.id || '').trim();

const normalizeRotationStudents = (students = [], preceptors = []) => {
    const preceptorById = new Map(
        (preceptors || []).map((preceptor) => [
            getRotationPreceptorId(preceptor),
            toDisplayText(preceptor?.name) || getDisplayNameFromUser(preceptor)
        ])
    );

    const copreceptedStudentIds = new Set();
    (preceptors || []).forEach((preceptor) => {
        (Array.isArray(preceptor?.students_data) ? preceptor.students_data : []).forEach((student) => {
            const studentId = getRotationStudentId(student);
            if (studentId && Boolean(student?.isCoprecepted)) {
                copreceptedStudentIds.add(studentId);
            }
        });
    });

    return (students || []).map((student, index) => {
        const preceptorId = toDisplayText(student?.preceptorId || student?.preceptor_id || '').trim();
        const preceptorName =
            toDisplayText(student?.preceptor_name || student?.preceptorName) ||
            preceptorById.get(preceptorId) ||
            '';
        const studentId = getRotationStudentId(student);

        return {
            ...student,
            _rowId: studentId || `student-${index}`,
            name: toDisplayText(student?.name) || getDisplayNameFromUser(student) || 'Unknown',
            email: toDisplayText(student?.email),
            rank: toDisplayText(student?.rank),
            phone: toDisplayText(student?.phone),
            specialty_name:
                student?.specialty_name ||
                student?.speciality_name ||
                student?.speciality ||
                '',
            institution_name: student?.institution_name || student?.institution || '',
            preceptor_name: preceptorName,
            preceptorId,
            isCoprecepted: Boolean(student?.isCoprecepted) || copreceptedStudentIds.has(studentId)
        };
    });
};

const normalizeRotationPreceptors = (preceptors = [], students = []) => {
    const studentsById = new Map(
        (students || []).map((student) => [
            getRotationStudentId(student),
            toDisplayText(student?.name) || getDisplayNameFromUser(student)
        ])
    );

    return (preceptors || []).map((preceptor, index) => {
        const linkedStudentIds = Array.isArray(preceptor?.students) ? preceptor.students : [];
        const studentRowsFromData = Array.isArray(preceptor?.students_data) ? preceptor.students_data : [];

        const fallbackStudentRows = linkedStudentIds.map((rawId) => {
            const idText = toDisplayText(rawId).trim();
            const isCoprecepted = idText.toLowerCase().startsWith('co-');
            const cleanId = isCoprecepted ? idText.slice(3) : idText;
            const mappedName = studentsById.get(cleanId);
            return {
                studentId: cleanId,
                name: mappedName || cleanId || 'Unknown student',
                isCoprecepted
            };
        });

        const normalizedStudents =
            studentRowsFromData.length > 0
                ? studentRowsFromData.map((student) => ({
                    ...student,
                    studentId: toDisplayText(student?.studentId || student?._id || student?.id || '').trim(),
                    name: toDisplayText(student?.name) || getDisplayNameFromUser(student) || 'Unknown student',
                    isCoprecepted: Boolean(student?.isCoprecepted)
                }))
                : fallbackStudentRows;

        return {
            ...preceptor,
            _rowId: getRotationPreceptorId(preceptor) || `preceptor-${index}`,
            name: toDisplayText(preceptor?.name) || getDisplayNameFromUser(preceptor) || 'Unknown',
            email: toDisplayText(preceptor?.email),
            rank: toDisplayText(preceptor?.rank),
            phone: toDisplayText(preceptor?.phone),
            students_data: normalizedStudents,
            students_count: normalizedStudents.length,
            student_details: normalizedStudents
                .map((student) => student.name)
                .filter(Boolean)
                .join(', ')
        };
    });
};

// Check if two date ranges intersect
const dateRangesIntersect = (range1Start, range1End, range2Start, range2End) => {
    const start1 = parseDate(range1Start);
    const end1 = parseDate(range1End);
    const start2 = parseDate(range2Start);
    const end2 = parseDate(range2End);
    
    if (!start1 || !end1 || !start2 || !end2) return false;
    
    return start1 <= end2 && start2 <= end1;
};

// Get curriculum display name
// Moved getCurriculumDisplayName function inside component to access curriculums state

// Get curriculum year from date
const getCurriculumYear = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    return month >= 7 ? year : year - 1;
};

// Get number of rotations from availability info
const getNumberOfRotations = (availabilityInfo) => {
    let maxRotations = 0;
    Object.values(availabilityInfo).forEach(info => {
        const totalAvail = info.totalAvailability;
        if (Array.isArray(totalAvail)) {
            maxRotations = Math.max(maxRotations, totalAvail.length);
        } else {
            Object.values(totalAvail).forEach(yearData => {
                maxRotations = Math.max(maxRotations, Object.keys(yearData).length);
            });
        }
    });
    return maxRotations;
};

// Preceptor Filters Component
const ChangePreceptorFilters = ({ preceptors, onFilter }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSpeciality, setSelectedSpeciality] = useState('');

    useEffect(() => {
        if (!preceptors) {
            onFilter([]);
            return;
        }

        const allPreceptors = Object.values(preceptors).flatMap(group => group.preceptors || []);
        
        let filtered = allPreceptors.filter(preceptor => {
            const matchesSearch = searchTerm === '' || 
                preceptor.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                preceptor.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                preceptor.email?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesSpeciality = selectedSpeciality === '' || 
                preceptor.speciality === selectedSpeciality;
            
            return matchesSearch && matchesSpeciality;
        });

        onFilter(filtered);
    }, [preceptors, searchTerm, selectedSpeciality, onFilter]);

    if (!preceptors) return null;

    const specialities = [...new Set(Object.values(preceptors).map(group => group.speciality))];

    return (
        <div style={{
            background: 'var(--secondary-color)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            border: '1px solid var(--border-color)'
        }}>
            <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
            }}>
                Filter Preceptors
            </h3>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
            }}>
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.9rem',
                        background: 'var(--white)',
                        color: 'var(--text-primary)'
                    }}
                />
                <select
                    value={selectedSpeciality}
                    onChange={e => setSelectedSpeciality(e.target.value)}
                    style={{
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        fontSize: '0.9rem',
                        background: 'var(--white)',
                        color: 'var(--text-primary)'
                    }}
                >
                    <option value="">All Specialities</option>
                    {specialities.map(speciality => (
                        <option key={speciality} value={speciality}>{speciality}</option>
                    ))}
                </select>
            </div>
        </div>
    );
};

// Column Selector Component
const ColumnSelector = ({ availableColumns, visibleColumns, onColumnToggle, onExportTable }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpen]);

    const getColumnLabel = (column) => {
        const labels = {
            'rotation': 'Rotation',
            'date_range': 'Date Range',
            'dateRange': 'Date Range', 
            'students': 'Students',
            'name': 'Student Name',
            'email': 'Email',
            'role': 'My Role',
            'speciality': 'Rotation/Experience',
            'student': 'Student',
            'preceptor_name': 'Preceptor Name',
            'start_date': 'Start Date',
            'end_date': 'End Date',
            'institution': 'Institution',
            'primary_preceptor': 'Primary Preceptor',
            'co_preceptor': 'Co-Preceptor',
            'preceptor_role': 'Preceptor Role'
        };
        return labels[column] || column.charAt(0).toUpperCase() + column.slice(1);
    };

    const exportButtonStyle = {
        background: 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)',
        color: '#134b5f',
        border: '1.5px solid #7aa7b6',
        borderRadius: 14,
        padding: '14px 32px',
        fontWeight: 600,
        fontSize: 16,
        letterSpacing: 0.5,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        outline: 'none',
        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    };

    return (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <div className="column-selector-container" style={{ position: 'relative' }} ref={dropdownRef}>
                <button 
                    className="column-selector-button"
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        background: '#fff',
                        color: '#2b4663',
                        border: '2px solid #2b4663',
                        borderRadius: 12,
                        padding: '14px 32px',
                        fontWeight: 600,
                        fontSize: 16,
                        letterSpacing: 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        outline: 'none',
                        boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
                        e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
                        e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
                    }}
                >
                    <span style={{ fontSize: '1rem' }}>📊</span>
                    <span>Customize Columns</span>
                    <span style={{ 
                        fontSize: '0.75rem', 
                        transition: 'transform 0.2s',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>▼</span>
                </button>
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: '0',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '320px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        marginTop: '0.5rem',
                        animation: 'modalFadeIn 0.2s ease-out'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                            padding: '1.25rem',
                            borderRadius: '16px 16px 0 0',
                            color: '#fff'
                        }}>
                            <h4 style={{
                                margin: 0,
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: '#ffffff'
                            }}>
                                <span>🎯</span>
                                Select Table Columns
                            </h4>
                            <p style={{
                                margin: '0.5rem 0 0 0',
                                fontSize: '0.85rem',
                                opacity: 0.9
                            }}>
                                Choose which columns to display in your table
                            </p>
                        </div>
                        <div style={{ padding: '1.5rem' }}>
                            {availableColumns.map(column => (
                                <label key={column} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.75rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderRadius: '10px',
                                    marginBottom: '0.5rem',
                                    background: visibleColumns.includes(column) ? '#f0f9ff' : 'transparent',
                                    border: `2px solid ${visibleColumns.includes(column) ? '#3b82f6' : 'transparent'}`
                                }}
                                onMouseEnter={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = '#f8fafc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!visibleColumns.includes(column)) {
                                        e.target.style.background = 'transparent';
                                    }
                                }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={visibleColumns.includes(column)}
                                        onChange={() => onColumnToggle(column)}
                                        style={{
                                            width: '18px',
                                            height: '18px',
                                            cursor: 'pointer',
                                            accentColor: '#3b82f6'
                                        }}
                                    />
                                    <span style={{
                                        color: '#374151',
                                        fontSize: '0.95rem',
                                        fontWeight: visibleColumns.includes(column) ? '600' : '500'
                                    }}>
                                        {getColumnLabel(column)}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Adjust the container width to be larger and responsive
// Full-bleed page layout: same size/space as Curriculum Management (no inner small container)
const viewAdminSchedulesPageStyle = {
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: '2rem',
    background: '#ffffff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// Ensure the container grows consistently with the table
const tableContainerStyle = {
    display: 'block',
    overflowX: 'auto',
    width: '100%',
    boxSizing: 'border-box',
};

const ViewAdminSchedules = ({ scheduleType }) => {
    const { showError: showToastError, showSuccess: showToastSuccess } = useToast();
    // When scheduleType prop is set, this page shows only that type (no type selector)
    const [allData, setAllData] = useState({
        students: [],
        preceptors: [],
        preceptorsWithSpecialities: null
    });
    const [selectedType, setSelectedType] = useState(scheduleType || '');
    const [selectedId, setSelectedId] = useState('');
    const [selectedIds, setSelectedIds] = useState([]); // Multi-selection support
    const [schedule, setSchedule] = useState([]);
    const [displaySchedule, setDisplaySchedule] = useState([]);
    const [multiSchedules, setMultiSchedules] = useState({}); // For multi-selection results
    const [loading, setLoading] = useState(false);
    const [fetchingSchedule, setFetchingSchedule] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editingRotation, setEditingRotation] = useState(null);
    const [availablePreceptors, setAvailablePreceptors] = useState([]);
    // Preceptor/co-preceptor assign modal: show list with availability and inline edit.
    // For the student "Change Preceptor" flow, `list` holds one ROW PER MATCHING ENTITY
    // (speciality or institution) a preceptor holds — a preceptor with 3 experiences
    // appears as 3 rows, one per experience — grouped/filtered by whichever entity type
    // this curriculum's rank actually matches on (`matchBy`), not both at once.
    const [preceptorAssignModal, setPreceptorAssignModal] = useState({
        open: false,
        type: 'preceptor', // 'preceptor' | 'copreceptor'
        rotationIndex: null,
        rotationNumber: null,
        curriculumId: '',
        curriculumName: '',
        studentId: null,
        primaryPreceptorId: null,
        list: [],
        loading: false,
        // 'experience' | 'institution' | null — drives which single filter/grouping
        // shows in the picker. null means "unknown / show legacy dual filters" (used
        // by the non-student branches, which don't resolve matchBy server-side).
        matchBy: null,
        entityNames: {},
        savingAvailabilityFor: null, // rowKey (`${preceptorId}::${entityId}`) currently saving
        editingAvailabilityFor: null, // rowKey currently being edited
        editAvailabilityDraft: [],
        currentAssignedPreceptorId: null,
        currentAssignedEntityId: null,
        search: '',
        filterSpecialityId: '',
        filterInstitutionId: ''
    });
    const [newSchedule, setNewSchedule] = useState([]);
    const [years, setYears] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [curriculums, setCurriculums] = useState([]);
    const [loadingCurriculums, setLoadingCurriculums] = useState(false);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear().toString());
    const [schedulesByYear, setSchedulesByYear] = useState({});
    const [addingCoPreceptor, setAddingCoPreceptor] = useState(null);
    const [coPreceptors, setCoPreceptors] = useState({});
    const [studentInfo, setStudentInfo] = useState(null);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingPreceptors, setLoadingPreceptors] = useState(false);
    const [pendingCoPreceptorChanges, setPendingCoPreceptorChanges] = useState({});
    const [loadingPreceptorOptions, setLoadingPreceptorOptions] = useState(false);
    const [loadingCoPreceptorOptions, setLoadingCoPreceptorOptions] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filteredPreceptors, setFilteredPreceptors] = useState([]);
    const [specialitySchedules, setSpecialitySchedules] = useState({});
    const [availabilityInfo, setAvailabilityInfo] = useState({});
    const [selectedRotationDetails, setSelectedRotationDetails] = useState(null);
    const [dateRangeLoading, setDateRangeLoading] = useState(false);
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [selectedRotations, setSelectedRotations] = useState([]);
    const [rotationSchedulesByNumber, setRotationSchedulesByNumber] = useState({});
    const [institutionScheduleData, setInstitutionScheduleData] = useState({});
    const [specialityScheduleData, setSpecialityScheduleData] = useState({});
    const [configurations, setConfigurations] = useState({});
    const [loadingConfigurations, setLoadingConfigurations] = useState(false);
    const [selectedSpecialities, setSelectedSpecialities] = useState([]);
    const [selectedInstitutions, setSelectedInstitutions] = useState([]);
    const [availableSpecialities, setAvailableSpecialities] = useState([]);
    const [availableInstitutions, setAvailableInstitutions] = useState([]);
    const [rotationFilter, setRotationFilter] = useState([]);
    const [preceptorNameFilter, setPreceptorNameFilter] = useState('');
    const [advancedSearch, setAdvancedSearch] = useState('');
    const [currentCurriculums, setCurrentCurriculums] = useState([]);
    const [currentCurriculum, setCurrentCurriculum] = useState('');
    const [selectedCurriculums, setSelectedCurriculums] = useState([]); // Multi-curriculum selection
    const [showCurrentOnly, setShowCurrentOnly] = useState(false);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [matrixDirtyByCurriculum, setMatrixDirtyByCurriculum] = useState({});

    const resolveCurriculumIdsForMode = useCallback((mode, explicitIds = null) => {
        const raw = Array.isArray(explicitIds)
            ? explicitIds
            : (mode === 'student' || mode === 'preceptor')
                ? selectedCurriculums
                : selectedYears;

        const normalized = Array.from(
            new Set(
                (raw || [])
                    .map((id) => (id == null ? '' : String(id).trim()))
                    .filter(Boolean)
            )
        );

        if (normalized.length > 0) {
            return normalized;
        }

        return Array.from(
            new Set(
                (curriculums || [])
                    .map((c) => (c?._id == null ? '' : String(c._id).trim()))
                    .filter(Boolean)
            )
        );
    }, [selectedCurriculums, selectedYears, curriculums]);

    const buildCurriculumStudentRecord = useCallback((student, curriculumId) => {
        const normalized = normalizeUser(student || {});
        const rawId = normalized._id ?? student?._id ?? student?.studentId ?? student?.id;
        if (!rawId) return null;
        const id = String(rawId);

        return {
            _id: id,
            firstname: normalized.firstname || student?.firstname || '',
            lastname: normalized.lastname || student?.lastname || '',
            email: normalized.email || student?.email || '',
            rank: normalized.rank || student?.rank || '',
            curriculumIds: curriculumId ? [String(curriculumId)] : [],
            fullData: {
                ...normalized,
                _id: id,
                curriculumIds: curriculumId ? [String(curriculumId)] : []
            }
        };
    }, []);

    const buildCurriculumPreceptorRecord = useCallback((preceptor, curriculumId) => {
        const normalized = normalizeUser(preceptor || {});
        const rawId = normalized._id ?? preceptor?._id ?? preceptor?.preceptorId ?? preceptor?.id;
        if (!rawId) return null;
        const id = String(rawId);

        const availabilityArray = Array.isArray(preceptor?.availabilities) ? preceptor.availabilities : null;
        const rotationsAvailabilities = curriculumId && availabilityArray
            ? { [String(curriculumId)]: availabilityArray }
            : normalized.rotationsAvailabilities;

        return {
            _id: id,
            firstname: normalized.firstname || preceptor?.firstname || '',
            lastname: normalized.lastname || preceptor?.lastname || '',
            email: normalized.email || preceptor?.email || '',
            rank: normalized.rank || preceptor?.rank || '',
            specialityId: normalized.specialityId || preceptor?.specialityId || '',
            institutionId: normalized.institutionId || preceptor?.institutionId || '',
            speciality: normalized.speciality || preceptor?.speciality || null,
            institution: normalized.institution || preceptor?.institution || null,
            curriculumIds: curriculumId ? [String(curriculumId)] : [],
            rotationsAvailabilities,
            fullData: {
                ...normalized,
                _id: id,
                preceptorId: id,
                curriculumIds: curriculumId ? [String(curriculumId)] : [],
                rotationsAvailabilities
            }
        };
    }, []);

    const getCurriculumRotationCount = useCallback((curriculumId) => {
        if (!curriculumId) return null;
        const config = configurations[String(curriculumId)] || configurations[curriculumId];
        if (!config) return null;
        const fromRotations = Array.isArray(config.rotations) ? config.rotations.length : 0;
        const fromCount = parseInt(config.rotationsCount, 10);
        const fromNumber = parseInt(config.rotationsNumber, 10);
        const resolved = fromRotations || fromCount || fromNumber;
        return Number.isInteger(resolved) && resolved > 0 ? resolved : null;
    }, [configurations]);

    const clipScheduleArray = useCallback((rotations, curriculumId) => {
        if (!Array.isArray(rotations)) return [];
        const count = getCurriculumRotationCount(curriculumId);
        return count ? rotations.slice(0, count) : rotations;
    }, [getCurriculumRotationCount]);

    const clipScheduleMap = useCallback((scheduleMap) => {
        if (!scheduleMap || typeof scheduleMap !== 'object') return {};
        const clipped = {};
        Object.entries(scheduleMap).forEach(([curriculumId, rotations]) => {
            clipped[curriculumId] = clipScheduleArray(Array.isArray(rotations) ? rotations : [], curriculumId);
        });
        return clipped;
    }, [clipScheduleArray]);

    useEffect(() => {
        if (startDate && endDate && endDate < startDate) {
            setEndDate('');
        }
    }, [startDate, endDate]);

    const selectedStudentId = useMemo(() => {
        if (!selectedStudent) return null;
        return String(selectedStudent);
    }, [selectedStudent]);

    const selectedStudentName = useMemo(() => {
        if (!selectedStudentId) return '';

        const fromAvailable = availableStudents.find((student) => String(student.id) === String(selectedStudentId));
        if (fromAvailable?.name) {
            return fromAvailable.name;
        }

        const fromAllStudents = allData.students.find((student) => {
            const id = student?._id || student?.fullData?._id;
            return id && String(id) === String(selectedStudentId);
        });
        return getStudentDisplayName(fromAllStudents) || '';
    }, [selectedStudentId, availableStudents, allData.students]);

    const hasUnsavedMatrixChanges = useMemo(
        () => Object.values(matrixDirtyByCurriculum).some(Boolean),
        [matrixDirtyByCurriculum]
    );

    const handleMatrixDirtyChange = useCallback((curriculumKey, isDirty) => {
        if (!curriculumKey) return;
        setMatrixDirtyByCurriculum((prev) => {
            if (prev[curriculumKey] === isDirty) {
                return prev;
            }
            return {
                ...prev,
                [curriculumKey]: isDirty
            };
        });
    }, []);

    const handleSelectedStudentChange = (nextStudent) => {
        const normalized = nextStudent || null;
        if (normalized === selectedStudent) {
            return;
        }

        if (hasUnsavedMatrixChanges) {
            const proceed = window.confirm(
                'You have unsaved schedule changes for the currently selected student. Switching students will discard those changes. Continue?'
            );
            if (!proceed) {
                return;
            }
        }

        setMatrixDirtyByCurriculum({});
        setSelectedStudent(normalized);
    };

    const getScheduleTitle = (mode) => {
        switch (mode) {
            case 'student': return 'View Student Schedules';
            case 'preceptor': return 'View Preceptor Schedules';
            case 'speciality': return 'View Rotation/Experience Schedules';
            case 'institution': return 'View Institution Schedules';
            case 'rotation': return 'View Rotation Schedules';
            default: return 'View Admin Schedules';
        }
    };

    const getScheduleSubtitle = (mode) => {
        switch (mode) {
            case 'student': return 'View and manage student schedules across all curricula';
            case 'preceptor': return 'View and manage preceptor schedules across all curricula';
            case 'speciality': return 'View and manage rotation/experience schedules across all curricula';
            case 'institution': return 'View and manage institution schedules across all curricula';
            case 'rotation': return 'View and manage rotation schedules across all curricula';
            default: return 'View and manage schedules across all curricula';
        }
    };

    const getStudentDisplayName = (student) => {
        if (!student) return '';
        const first = student.firstname ? String(student.firstname).trim() : '';
        const last = student.lastname ? String(student.lastname).trim() : '';
        const fullName = `${first} ${last}`.trim();
        return fullName || (student.fullData?.name ? String(student.fullData.name).trim() : '');
    };

    const extractStudentsByCurriculum = (curriculumIds = []) => {
        if (!Array.isArray(curriculumIds) || curriculumIds.length === 0) return [];
        const targetIds = curriculumIds.map(String);

        const students = allData.students
            .filter((student) => {
                const ids = getScheduleCurriculumIds(student, 'student').map(String);
                return ids.some((id) => targetIds.includes(id));
            })
            .map((student) => {
                const id = student?._id || student?.fullData?._id;
                const name = getStudentDisplayName(student);
                if (!id || !name) {
                    return null;
                }
                return { id: String(id), name };
            })
            .filter(Boolean);

        const uniqueById = new Map();
        students.forEach((student) => {
            if (!uniqueById.has(student.id)) {
                uniqueById.set(student.id, student);
            }
        });

        return Array.from(uniqueById.values()).sort((a, b) => a.name.localeCompare(b.name));
    };

    const headerTitle = getScheduleTitle(selectedType || scheduleType);

    // Column management state
    // Update the default visible columns to disable 'daterange' and 'email'
    const [visibleColumns, setVisibleColumns] = useState({
        student: ['userName', 'curriculumName', 'rotation', 'primary_preceptor', 'co_preceptor', 'speciality', 'institution'],
        preceptor: ['userName', 'curriculumName', 'rotation', 'students'],
        speciality: ['Name', 'Preceptor', 'Rotation', 'Start Date', 'End Date'],
        institution: ['Name', 'Preceptor', 'Rotation', 'Start Date', 'End Date'],
        rotation: ['name', 'rank', 'phone', 'speciality', 'institution', 'preceptor_name'],
        rotationStudents: ['name', 'rank', 'phone', 'speciality', 'institution', 'preceptor_name'],
        rotationPreceptors: ['name', 'rank', 'phone', 'students_count', 'student_details']
    });

    const [availableColumns, setAvailableColumns] = useState({
        student: ['userName', 'curriculumName', 'rotation', 'date_range', 'primary_preceptor', 'co_preceptor', 'email', 'speciality', 'institution'],
        preceptor: ['userName', 'curriculumName', 'rotation', 'dateRange', 'students'],
        speciality: ['Name', 'Email', 'Preceptor', 'Rotation', 'Start Date', 'End Date'],
        institution: ['Name', 'Email', 'Preceptor', 'Rotation', 'Start Date', 'End Date'],
        rotation: ['name', 'email', 'rank', 'phone', 'speciality', 'institution', 'preceptor_name'],
        rotationStudents: ['name', 'email', 'rank', 'phone', 'speciality', 'institution', 'preceptor_name'],
        rotationPreceptors: ['name', 'email', 'rank', 'phone', 'students_count', 'student_details']
    });

    // Sorting state
    const [sortState, setSortState] = useState({
        student: { column: '', direction: 'asc' },
        preceptor: { column: '', direction: 'asc' },
        speciality: { column: '', direction: 'asc' },
        institution: { column: '', direction: 'asc' },
        rotation: { column: '', direction: 'asc' },
        rotationStudents: { column: '', direction: 'asc' },
        rotationPreceptors: { column: '', direction: 'asc' }
    });

    // Co-preceptor modal state
    const [coPreceptorModal, setCoPreceptorModal] = useState({
        isOpen: false,
        rotation: null,
        availablePreceptors: []
    });

    // Helper function to get curriculum display name
    const getCurriculumDisplayName = (curriculumId) => {
        if (!curriculumId) return 'Unknown Curriculum';
        
        // Try to find the curriculum in fetched data first
        const curriculum = curriculums.find(c => c._id === curriculumId);
        if (curriculum) {
            return curriculum.name;
        }
        
        // Fallback to legacy logic for backward compatibility
        if (curriculumId.includes('_')) {
            return curriculumId.replace('_', ' ');
        } else {
            return `${curriculumId}/${parseInt(curriculumId) + 1}`;
        }
    };

    // get_schedule_by_id returns speciality as { name: "..." }; support legacy { speciality: "..." }
    const getRotationSpecialityName = (rotation) =>
        (rotation?.speciality?.name ?? rotation?.speciality?.speciality) ?? '';

    // Auto-fade success messages
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // Auto-fade error messages
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    // When rendered as a dedicated page (scheduleType prop), fetch base data on mount
    useEffect(() => {
        if (!scheduleType) return;
        if (curriculums.length === 0) fetchCurriculums();
        if (['rotation', 'speciality', 'institution'].includes(scheduleType) && Object.keys(configurations).length === 0) fetchConfigurations();
    }, [scheduleType]);

    useEffect(() => {
        if (!selectedType || curriculums.length === 0) return;

        if (selectedType === 'student') {
            fetchStudents(selectedCurriculums);
            return;
        }

        if (selectedType === 'preceptor') {
            fetchPreceptors(selectedCurriculums);
            return;
        }

        if (selectedType === 'institution' || selectedType === 'speciality') {
            fetchStudents(selectedYears);
        }
    }, [selectedType, selectedCurriculums, selectedYears, curriculums]);

    // Fetch curriculum configurations for rotation mode
    const fetchConfigurations = async () => {
        if (loadingConfigurations) return; // Prevent concurrent calls
        
        setLoadingConfigurations(true);
        try {
            const response = await axiosInstance.get('/config/get_curriculum_configurations/');
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('📋 Configurations raw response:', response.data);

            const raw = response.data;
            const configData = {};
            // Backend returns a dict keyed by curriculum_id: { [curriculum_id]: config, ... }
            if (Array.isArray(raw)) {
                raw.forEach(config => {
                    const key = config?.curriculum_id || config?.year;
                    if (key) configData[key] = config;
                });
            } else if (raw && typeof raw === 'object') {
                Object.entries(raw).forEach(([key, config]) => {
                    configData[key] = config;
                });
            } else if (import.meta.env.VITE_DEBUG_MODE === 'true') {
                console.warn('Unrecognized configuration data shape:', raw);
            }

            setConfigurations(configData);
        } catch (err) {
            console.error('Error fetching configurations:', err);
            // Fallback to default years if configurations fail
            const currentYear = new Date().getFullYear();
            const defaultYears = {};
            for (let i = currentYear - 2; i <= currentYear + 2; i++) {
                defaultYears[i.toString()] = { year: i.toString(), rotations: 8 };
            }
            setConfigurations(defaultYears);
        } finally {
            setLoadingConfigurations(false);
        }
    };

    // Initialize data fetch only when needed - removed automatic calls on mount
    // Data will be fetched when specific modes are selected

    // Update available specialities and institutions
    useEffect(() => {
        if (selectedType === 'speciality') {
            const specialities = Object.entries(availabilityInfo).map(([id, info]) => ({
                id,
                name: info.name
            }));
            setAvailableSpecialities(specialities);
        } else if (selectedType === 'institution') {
            const institutions = Object.entries(availabilityInfo).map(([id, info]) => ({
                id,
                name: info.name
            }));
            setAvailableInstitutions(institutions);
        }
    }, [selectedType, availabilityInfo]);

    // Utility functions
    const showSuccess = (message) => {
        showToastSuccess(message);
    };

    const showError = (message) => {
        showToastError(message);
    };

    useEffect(() => {
        if (!error) return;
        showToastError(error);
        setError('');
    }, [error, showToastError]);

    useEffect(() => {
        if (!success) return;
        showToastSuccess(success);
        setSuccess('');
    }, [success, showToastSuccess]);

    // Advanced search helper function
    const applyAdvancedSearch = (data, searchTerm, mode) => {
        if (!searchTerm.trim()) return data;
        
        const searchLower = searchTerm.trim().toLowerCase();
        
        return data.filter(item => {
            switch (mode) {
                case 'student':
                    return (
                        (item.preceptor?.firstname || '').toLowerCase().includes(searchLower) ||
                        (item.preceptor?.lastname || '').toLowerCase().includes(searchLower) ||
                        (item.preceptor?.email || '').toLowerCase().includes(searchLower) ||
                        (getRotationSpecialityName(item) || '').toLowerCase().includes(searchLower) ||
                        (item.institution?.name || '').toLowerCase().includes(searchLower)
                    );
                    
                case 'preceptor':
                    return item.students && Array.isArray(item.students) && item.students.some(student => 
                        (student.name || '').toLowerCase().includes(searchLower) ||
                        (student.email || '').toLowerCase().includes(searchLower)
                    );
                    
                case 'speciality':
                    return (
                        (item.name || '').toLowerCase().includes(searchLower) ||
                        (item.preceptor_name || '').toLowerCase().includes(searchLower) ||
                        (item.email || '').toLowerCase().includes(searchLower)
                    );
                    
                case 'institution':
                    return (
                        (item.name || '').toLowerCase().includes(searchLower) ||
                        (item.preceptor_name || '').toLowerCase().includes(searchLower) ||
                        (item.email || '').toLowerCase().includes(searchLower)
                    );
                    
                case 'rotation':
                    return (
                        (item.name || '').toLowerCase().includes(searchLower) ||
                        (item.firstname || '').toLowerCase().includes(searchLower) ||
                        (item.lastname || '').toLowerCase().includes(searchLower) ||
                        (item.preceptor_name || '').toLowerCase().includes(searchLower) ||
                        (item.email || '').toLowerCase().includes(searchLower) ||
                        (item.speciality || '').toLowerCase().includes(searchLower) ||
                        (item.institution || '').toLowerCase().includes(searchLower)
                    );
                    
                default:
                    return true;
            }
        });
    };

    // Sortable Table Header Component
    const SortableTableHeader = ({ label, columnKey, sortState, onSort }) => {
        const isActive = sortState.column === columnKey;
        const direction = isActive ? sortState.direction : undefined;
        return (
            <th
                onClick={() => onSort(columnKey)}
                style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none', 
                    position: 'relative', 
                    transition: 'all 0.2s', 
                    color: '#fff',
                    fontWeight: isActive ? '700' : '600',
                    textShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.3)'
                }}
                className="sortable-header"
            >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {label}
                    <span style={{ fontSize: '1rem', marginLeft: 2, display: 'inline-block', width: 16 }}>
                        {isActive ? (
                            direction === 'asc' ? '▲' : '▼'
                        ) : (
                            <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>▲▼</span>
                        )}
                    </span>
                </span>
            </th>
        );
    };

    // Sorting handler
    const handleSort = (mode, column) => {
        setSortState(prev => {
            const prevState = prev[mode];
            let direction = 'asc';
            if (prevState.column === column) {
                direction = prevState.direction === 'asc' ? 'desc' : 'asc';
            }
            return { ...prev, [mode]: { column, direction } };
        });
    };

    // Sorting logic
    function getSortedData(data, mode) {
        const sortConfig = sortState[mode];
        if (!sortConfig || !sortConfig.column) return data;
        
        const { column, direction } = sortConfig;
        
        const sorted = [...data].sort((a, b) => {
            let aValue, bValue;
            
            // Custom logic for nested fields based on mode
            if (mode === 'student') {
                if (column === 'rotation') {
                    aValue = a.rotation !== undefined ? parseInt(a.rotation) : data.indexOf(a);
                    bValue = b.rotation !== undefined ? parseInt(b.rotation) : data.indexOf(b);
                } else if (column === 'date_range') {
                    const aDate = a.rotationStartDate ? parseMongoDate(a.rotationStartDate) : null;
                    const bDate = b.rotationStartDate ? parseMongoDate(b.rotationStartDate) : null;
                    
                    if (!aDate && !bDate) {
                        aValue = 0;
                        bValue = 0;
                    } else if (!aDate) {
                        aValue = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
                        bValue = bDate ? bDate.getTime() : 0;
                    } else if (!bDate) {
                        aValue = aDate ? aDate.getTime() : 0;
                        bValue = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
                    } else {
                        aValue = aDate.getTime();
                        bValue = bDate.getTime();
                    }
                } else if (column === 'primary_preceptor') {
                    aValue = a.preceptor ? `${a.preceptor.firstname} ${a.preceptor.lastname}` : '';
                    bValue = b.preceptor ? `${b.preceptor.firstname} ${b.preceptor.lastname}` : '';
                } else if (column === 'co_preceptor') {
                    aValue = a.copreceptor ? `${a.copreceptor.firstname} ${a.copreceptor.lastname}` : '';
                    bValue = b.copreceptor ? `${b.copreceptor.firstname} ${b.copreceptor.lastname}` : '';
                } else if (column === 'email') {
                    aValue = a.preceptor?.email || '';
                    bValue = b.preceptor?.email || '';
                } else if (column === 'speciality') {
                    aValue = getRotationSpecialityName(a);
                    bValue = getRotationSpecialityName(b);
                } else if (column === 'institution') {
                    aValue = a.institution?.name || '';
                    bValue = b.institution?.name || '';
                } else {
                    aValue = a[column] || '';
                    bValue = b[column] || '';
                }
            } else if (mode === 'preceptor') {
                if (column === 'students') {
                    aValue = (a && Array.isArray(a.students)) ? a.students.length : 0;
                    bValue = (b && Array.isArray(b.students)) ? b.students.length : 0;
                } else if (column === 'rotation') {
                    aValue = (a && a.rotation !== undefined) ? parseInt(a.rotation) : data.indexOf(a);
                    bValue = (b && b.rotation !== undefined) ? parseInt(b.rotation) : data.indexOf(b);
                } else if (column === 'dateRange') {
                    const aDate = a && a.rotationStartDate ? parseMongoDate(a.rotationStartDate) : null;
                    const bDate = b && b.rotationStartDate ? parseMongoDate(b.rotationStartDate) : null;
                    
                    if (!aDate && !bDate) {
                        aValue = 0;
                        bValue = 0;
                    } else if (!aDate) {
                        aValue = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
                        bValue = bDate ? bDate.getTime() : 0;
                    } else if (!bDate) {
                        aValue = aDate ? aDate.getTime() : 0;
                        bValue = direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
                    } else {
                        aValue = aDate.getTime();
                        bValue = bDate.getTime();
                    }
                } else {
                    aValue = a[column] || '';
                    bValue = b[column] || '';
                }
            } else if (mode === 'rotation') {
                // Handle sorting for rotation mode student data
                if (column === 'name') {
                    aValue = a.name || '';
                    bValue = b.name || '';
                } else if (column === 'email') {
                    aValue = a.email || '';
                    bValue = b.email || '';
                } else if (column === 'rank') {
                    aValue = a.rank || '';
                    bValue = b.rank || '';
                } else if (column === 'phone') {
                    aValue = a.phone || '';
                    bValue = b.phone || '';
                } else if (column === 'specialty_name') {
                    aValue = a.specialty_name ? (a.specialty_name.speciality || a.specialty_name) : '';
                    bValue = b.specialty_name ? (b.specialty_name.speciality || b.specialty_name) : '';
                } else if (column === 'institution_name') {
                    aValue = a.institution_name ? (a.institution_name.name || a.institution_name) : '';
                    bValue = b.institution_name ? (b.institution_name.name || b.institution_name) : '';
                } else if (column === 'preceptor_name') {
                    aValue = a.preceptor_name || '';
                    bValue = b.preceptor_name || '';
                } else {
                    aValue = a[column] || '';
                    bValue = b[column] || '';
                }
            } else {
                aValue = a[column] || '';
                bValue = b[column] || '';
            }
            
            // Handle null/undefined values
            if (aValue === undefined || aValue === null) aValue = '';
            if (bValue === undefined || bValue === null) bValue = '';
            
            // Handle numeric values
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            // Handle string comparisons
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (direction === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
        
        return sorted;
    }

    // Column toggle handler
    const handleColumnToggle = (column) => {
        setVisibleColumns(prev => ({
            ...prev,
            [selectedType]: prev[selectedType].includes(column)
                ? prev[selectedType].filter(col => col !== column)
                : [...prev[selectedType], column]
        }));
    };

    // Column toggle handler for rotation students
    const handleRotationStudentsColumnToggle = (column) => {
        setVisibleColumns(prev => ({
            ...prev,
            rotationStudents: prev.rotationStudents.includes(column)
                ? prev.rotationStudents.filter(col => col !== column)
                : [...prev.rotationStudents, column]
        }));
    };

    // Column toggle handler for rotation preceptors
    const handleRotationPreceptorsColumnToggle = (column) => {
        setVisibleColumns(prev => ({
            ...prev,
            rotationPreceptors: prev.rotationPreceptors.includes(column)
                ? prev.rotationPreceptors.filter(col => col !== column)
                : [...prev.rotationPreceptors, column]
        }));
    };

    // Export function for rotation students table
    const exportRotationStudentsTable = (studentsData, rotationNumber) => {
        const normalizedStudents = normalizeRotationStudents(studentsData);
        const columns = visibleColumns.rotationStudents.map(col => {
            switch(col) {
                case 'name': return { key: 'name', label: 'Student Name' };
                case 'email': return { key: 'email', label: 'Email' };
                case 'rank': return { key: 'rank', label: 'Rank' };
                case 'phone': return { key: 'phone', label: 'Phone' };
                case 'speciality': return { key: 'specialty_name', label: 'Experience' };
                case 'institution': return { key: 'institution_name', label: 'Institution' };
                case 'preceptor_name': return { key: 'preceptor_name', label: 'Preceptor' };
                default: return { key: col, label: col };
            }
        });
        
        const renderedData = normalizedStudents.map(student => ({
            name: student.name,
            email: student.email,
            rank: student.rank || 'Not specified',
            phone: student.phone || 'Not provided',
            specialty_name: student.specialty_name?.speciality || student.specialty_name || 'Not assigned',
            institution_name: student.institution_name?.name || student.institution_name || 'Not assigned',
            preceptor_name: student.preceptor_name || 'Not assigned'
        }));

        exportRenderedTableData(
            renderedData,
            columns,
            `Rotation ${rotationNumber + 1} - Students Schedule`,
            `rotation_${rotationNumber + 1}_students_${new Date().toISOString().split('T')[0]}`,
            'CSV'
        );
    };

    // Export function for rotation preceptors table
    const exportRotationPreceptorsTable = (preceptorsData, rotationNumber) => {
        const normalizedPreceptors = normalizeRotationPreceptors(preceptorsData);
        const columns = visibleColumns.rotationPreceptors.map(col => {
            switch(col) {
                case 'name': return { key: 'name', label: 'Preceptor Name' };
                case 'email': return { key: 'email', label: 'Email' };
                case 'rank': return { key: 'rank', label: 'Rank' };
                case 'phone': return { key: 'phone', label: 'Phone' };
                case 'students_count': return { key: 'students_count', label: 'Students Count' };
                case 'student_details': return { key: 'student_details', label: 'Student Names' };
                default: return { key: col, label: col };
            }
        });
        
        const renderedData = normalizedPreceptors.map(preceptor => ({
            name: preceptor.name,
            email: preceptor.email,
            rank: preceptor.rank || 'Not specified',
            phone: preceptor.phone || 'Not provided',
            students_count: preceptor.students_count || 0,
            student_details: preceptor.students_data?.map(s => s.name).join(', ') || 'No students'
        }));

        exportRenderedTableData(
            renderedData,
            columns,
            `Rotation ${rotationNumber + 1} - Preceptors Schedule`,
            `rotation_${rotationNumber + 1}_preceptors_${new Date().toISOString().split('T')[0]}`,
            'CSV'
        );
    };

    // Handle export functionality
    const handleExport = (format) => {
        try {
            let renderedData = [];
            let columns = [];
            let title = '';

            const createColumns = (orderedKeys, labels) =>
                orderedKeys.map((key) => ({ key, label: labels[key] || key }));

            const getPreceptorDisplayName = (person) => {
                const firstName = person?.firstname != null && String(person.firstname) !== 'undefined'
                    ? String(person.firstname).trim()
                    : '';
                const lastName = person?.lastname != null && String(person.lastname) !== 'undefined'
                    ? String(person.lastname).trim()
                    : '';
                const fullName = `${firstName} ${lastName}`.trim();
                return fullName ? `Dr. ${fullName}` : '';
            };

            if (selectedType === 'student') {
                const filtered = applyAdvancedSearch(displayScheduleFiltered, advancedSearch, 'student');
                const rows = getSortedData(filtered, 'student');
                const isMultiUser = selectedIds.length > 1;
                const selectedStudentData = allData.students.find((student) => student._id === selectedId);
                const selectedStudentName = selectedStudentData
                    ? `${selectedStudentData.firstname || ''} ${selectedStudentData.lastname || ''}`.trim()
                    : '';

                renderedData = rows.map((rotation, index) => ({
                    userName: rotation.userName || selectedStudentName,
                    curriculumName: rotation.curriculumName || getCurriculumDisplayName(rotation.curriculumId || currentYear),
                    rotation: `Rotation ${(rotation.rotation !== undefined ? rotation.rotation : index) + 1}`,
                    date_range:
                        rotation.rotationStartDate && rotation.rotationEndDate
                            ? `${formatDate(parseMongoDate(rotation.rotationStartDate))} - ${formatDate(parseMongoDate(rotation.rotationEndDate))}`
                            : '',
                    primary_preceptor: getPreceptorDisplayName(rotation.preceptor),
                    co_preceptor: getPreceptorDisplayName(rotation.copreceptor),
                    email: rotation.preceptor?.email || '',
                    speciality: getRotationSpecialityName(rotation) || '',
                    institution: rotation.institution?.name || ''
                }));

                const orderedKeys = (visibleColumns.student || []).filter((key) => {
                    if (!['userName', 'curriculumName', 'rotation', 'date_range', 'primary_preceptor', 'co_preceptor', 'email', 'speciality', 'institution'].includes(key)) {
                        return false;
                    }
                    if (!isMultiUser && (key === 'userName' || key === 'curriculumName')) {
                        return false;
                    }
                    return true;
                });
                columns = createColumns(orderedKeys, {
                    userName: 'Student',
                    curriculumName: 'Curriculum',
                    rotation: 'Rotation',
                    date_range: 'Date Range',
                    primary_preceptor: 'Primary Preceptor',
                    co_preceptor: 'Co-Preceptor',
                    email: 'Email',
                    speciality: 'Experience',
                    institution: 'Institution'
                });
                title = 'Admin Schedules - Student Schedules';
            } else if (selectedType === 'preceptor') {
                const preceptorRows = displayScheduleFiltered.filter(
                    (row) => row && typeof row === 'object' && row.rotation !== undefined
                );
                const filtered = applyAdvancedSearch(preceptorRows, advancedSearch, 'preceptor');
                const rows = getSortedData(filtered, 'preceptor');
                const isMultiUser = selectedIds.length > 1;
                const selectedPreceptorData = allData.preceptors.find((preceptor) => preceptor._id === selectedId);
                const selectedPreceptorName = selectedPreceptorData
                    ? `${selectedPreceptorData.firstname || ''} ${selectedPreceptorData.lastname || ''}`.trim()
                    : '';

                renderedData = rows.map((rotationData) => ({
                    userName: rotationData.userName || selectedPreceptorName,
                    curriculumName: rotationData.curriculumName || getCurriculumDisplayName(rotationData.curriculumId || currentYear),
                    rotation: `Rotation ${rotationData.rotation + 1}`,
                    dateRange:
                        rotationData.rotationStartDate && rotationData.rotationEndDate
                            ? `${formatDate(rotationData.rotationStartDate)} - ${formatDate(rotationData.rotationEndDate)}`
                            : '',
                    students: Array.isArray(rotationData.students)
                        ? rotationData.students
                              .map((student) => `${student.name || ''}${student.isCoprecepted ? ' (Co)' : ' (Pr)'}`.trim())
                              .filter(Boolean)
                              .join(', ')
                        : ''
                }));

                const orderedKeys = (visibleColumns.preceptor || []).filter((key) => {
                    if (!['userName', 'curriculumName', 'rotation', 'dateRange', 'students'].includes(key)) {
                        return false;
                    }
                    if (!isMultiUser && (key === 'userName' || key === 'curriculumName')) {
                        return false;
                    }
                    return true;
                });
                columns = createColumns(orderedKeys, {
                    userName: 'Preceptor',
                    curriculumName: 'Curriculum',
                    rotation: 'Rotation',
                    dateRange: 'Date Range',
                    students: 'Students'
                });
                title = 'Admin Schedules - Preceptor Schedules';
            } else if (selectedType === 'rotation') {
                const rotationNumbers = [...selectedRotations].sort((a, b) => a - b);

                rotationNumbers.forEach((rotationNumber) => {
                    const rotationData = rotationSchedulesByNumber[rotationNumber] || {};
                    const normalizedStudents = normalizeRotationStudents(rotationData.students || [], rotationData.preceptors || []);
                    const normalizedPreceptors = normalizeRotationPreceptors(rotationData.preceptors || [], normalizedStudents);
                    const students = getSortedData(normalizedStudents, 'rotationStudents');
                    const preceptors = getSortedData(normalizedPreceptors, 'rotationPreceptors');

                    students.forEach((student) => {
                        renderedData.push({
                            entity_type: 'Student',
                            curriculum: getCurriculumDisplayName(currentYear),
                            rotation: `Rotation ${rotationNumber + 1}`,
                            name: student.name || '',
                            email: student.email || '',
                            rank: student.rank || '',
                            phone: student.phone || '',
                            speciality: toDisplayText(student.specialty_name) || '',
                            institution: toDisplayText(student.institution_name) || '',
                            preceptor: toDisplayText(student.preceptor_name) || '',
                            students_assigned: '',
                            student_names: ''
                        });
                    });

                    preceptors.forEach((preceptor) => {
                        const studentNames = Array.isArray(preceptor.students_data)
                            ? preceptor.students_data.map((student) => student.name).filter(Boolean)
                            : [];
                        renderedData.push({
                            entity_type: 'Preceptor',
                            curriculum: getCurriculumDisplayName(currentYear),
                            rotation: `Rotation ${rotationNumber + 1}`,
                            name: toDisplayText(preceptor.name) || '',
                            email: preceptor.email || '',
                            rank: preceptor.rank || '',
                            phone: preceptor.phone || '',
                            speciality: '',
                            institution: '',
                            preceptor: '',
                            students_assigned: String(studentNames.length),
                            student_names: studentNames.join(', ')
                        });
                    });
                });

                columns = createColumns(
                    [
                        'entity_type',
                        'curriculum',
                        'rotation',
                        'name',
                        'email',
                        'rank',
                        'phone',
                        'speciality',
                        'institution',
                        'preceptor',
                        'students_assigned',
                        'student_names'
                    ],
                    {
                        entity_type: 'Type',
                        curriculum: 'Curriculum',
                        rotation: 'Rotation',
                        name: 'Name',
                        email: 'Email',
                        rank: 'Rank',
                        phone: 'Phone',
                        speciality: 'Experience',
                        institution: 'Institution',
                        preceptor: 'Preceptor',
                        students_assigned: 'Students Assigned',
                        student_names: 'Student Names'
                    }
                );
                title = `Admin Schedules - Rotation Schedules - ${getCurriculumDisplayName(currentYear)}`;
            } else if (selectedType === 'institution' || selectedType === 'speciality') {
                const sourceData = selectedType === 'institution' ? institutionScheduleData : specialityScheduleData;
                const itemKey = selectedType === 'institution' ? 'institutions' : 'specialities';
                const itemLabel = selectedType === 'institution' ? 'Institution' : 'Experience';

                Object.entries(sourceData).forEach(([curriculumKey, curriculumData]) => {
                    (curriculumData?.rotations || []).forEach((rotationData, index) => {
                        const rotationNumber = rotationData?.rotation !== undefined ? rotationData.rotation : index;
                        const items = rotationData?.[itemKey] || {};

                        Object.entries(items).forEach(([itemName, itemData]) => {
                            const preceptors = itemData?.preceptors || {};
                            const totalAvailability = itemData?.currentTotalAvailability ?? itemData?.totalAvailability ?? '';

                            if (Object.keys(preceptors).length === 0) {
                                renderedData.push({
                                    curriculum: getCurriculumDisplayName(curriculumKey),
                                    rotation: `Rotation ${rotationNumber + 1}`,
                                    item_name: itemName,
                                    preceptor: '',
                                    availability: '',
                                    total_availability: totalAvailability,
                                    assigned_students_count: '0',
                                    assigned_students: ''
                                });
                                return;
                            }

                            Object.entries(preceptors).forEach(([preceptorName, preceptorData]) => {
                                const assignedStudents = Array.isArray(preceptorData?.studentsDetailed) && preceptorData.studentsDetailed.length > 0
                                    ? preceptorData.studentsDetailed
                                          .map((student) => `${student.name || ''}${student.isCoprecepted ? ' (Co)' : ' (Pr)'}`.trim())
                                          .filter(Boolean)
                                    : Array.isArray(preceptorData?.students)
                                        ? preceptorData.students.filter(Boolean)
                                        : [];

                                renderedData.push({
                                    curriculum: getCurriculumDisplayName(curriculumKey),
                                    rotation: `Rotation ${rotationNumber + 1}`,
                                    item_name: itemName,
                                    preceptor: preceptorName,
                                    availability: preceptorData?.availability ?? '',
                                    total_availability: totalAvailability,
                                    assigned_students_count: String(assignedStudents.length),
                                    assigned_students: assignedStudents.join(', ')
                                });
                            });
                        });
                    });
                });

                columns = createColumns(
                    [
                        'curriculum',
                        'rotation',
                        'item_name',
                        'preceptor',
                        'availability',
                        'total_availability',
                        'assigned_students_count',
                        'assigned_students'
                    ],
                    {
                        curriculum: 'Curriculum',
                        rotation: 'Rotation',
                        item_name: itemLabel,
                        preceptor: 'Preceptor',
                        availability: 'Availability',
                        total_availability: 'Total Availability',
                        assigned_students_count: 'Assigned Students Count',
                        assigned_students: 'Assigned Students'
                    }
                );
                title =
                    selectedType === 'institution'
                        ? `Admin Schedules - Institution Schedules - ${currentYear ? getCurriculumDisplayName(currentYear) : 'All Years'}`
                        : `Admin Schedules - Experience Schedules - ${currentYear ? getCurriculumDisplayName(currentYear) : 'All Years'}`;
            } else {
                showError('Please select a schedule type and entity to export.');
                return;
            }

            if (renderedData.length === 0) {
                showError('No data available to export.');
                return;
            }

            if (!columns.length) {
                columns = Object.keys(renderedData[0]).map((key) => ({ key, label: key }));
            }

            const filename = `admin_schedules_${selectedType}_${new Date().toISOString().split('T')[0]}`;
            exportRenderedTableData(renderedData, columns, title, filename, format);
        } catch (error) {
            console.error('Export failed:', error);
            showError('Export failed. Please try again.');
        }
    };

    // Fetch functions
    const fetchStudents = async (curriculumIds = null) => {
        setLoadingStudents(true);
        try {
            const targetCurriculumIds = resolveCurriculumIdsForMode('student', curriculumIds);
            if (targetCurriculumIds.length === 0) {
                setAllData(prev => ({ ...prev, students: [] }));
                return;
            }

            const responses = await Promise.all(
                targetCurriculumIds.map((curriculumId) =>
                    axiosInstance
                        .get(`/curriculum/${encodeURIComponent(curriculumId)}/students`)
                        .then((response) => ({ curriculumId, data: response.data }))
                        .catch((error) => {
                            if (error?.response?.status === 404) {
                                return { curriculumId, data: { students: [] } };
                            }
                            throw error;
                        })
                )
            );

            const studentById = new Map();
            responses.forEach(({ curriculumId, data }) => {
                const source = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.students)
                        ? data.students
                        : [];

                source.forEach((student) => {
                    const built = buildCurriculumStudentRecord(student, curriculumId);
                    if (!built) return;

                    const existing = studentById.get(built._id);
                    if (!existing) {
                        studentById.set(built._id, built);
                        return;
                    }

                    const curriculumSet = new Set([
                        ...(existing.curriculumIds || []),
                        ...(built.curriculumIds || [])
                    ]);
                    const mergedCurriculumIds = Array.from(curriculumSet);

                    studentById.set(built._id, {
                        ...existing,
                        ...built,
                        curriculumIds: mergedCurriculumIds,
                        fullData: {
                            ...(existing.fullData || {}),
                            ...(built.fullData || {}),
                            curriculumIds: mergedCurriculumIds
                        }
                    });
                });
            });

            const students = Array.from(studentById.values()).sort((a, b) => {
                const aName = `${a.firstname || ''} ${a.lastname || ''}`.trim();
                const bName = `${b.firstname || ''} ${b.lastname || ''}`.trim();
                return aName.localeCompare(bName);
            });
            
            setAllData(prev => ({
                ...prev,
                students
            }));
        } catch (err) {
            console.error('Error fetching students:', err);
            setAllData(prev => ({ ...prev, students: [] }));
            setError('Failed to fetch students');
        } finally {
            setLoadingStudents(false);
        }
    };

    const fetchPreceptors = async (curriculumIds = null) => {
        setLoadingPreceptors(true);
        try {
            const targetCurriculumIds = resolveCurriculumIdsForMode('preceptor', curriculumIds);
            if (targetCurriculumIds.length === 0) {
                setAllData(prev => ({ ...prev, preceptors: [] }));
                return;
            }

            const responses = await Promise.all(
                targetCurriculumIds.map((curriculumId) =>
                    axiosInstance
                        .get(`/curriculum/${encodeURIComponent(curriculumId)}/preceptors`)
                        .then((response) => ({ curriculumId, data: response.data }))
                        .catch((error) => {
                            if (error?.response?.status === 404) {
                                return { curriculumId, data: { preceptors: [] } };
                            }
                            throw error;
                        })
                )
            );

            const preceptorById = new Map();
            responses.forEach(({ curriculumId, data }) => {
                const source = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.preceptors)
                        ? data.preceptors
                        : [];

                source.forEach((preceptor) => {
                    const built = buildCurriculumPreceptorRecord(preceptor, curriculumId);
                    if (!built) return;

                    const existing = preceptorById.get(built._id);
                    if (!existing) {
                        preceptorById.set(built._id, built);
                        return;
                    }

                    const curriculumSet = new Set([
                        ...(existing.curriculumIds || []),
                        ...(built.curriculumIds || [])
                    ]);
                    const mergedCurriculumIds = Array.from(curriculumSet);

                    preceptorById.set(built._id, {
                        ...existing,
                        ...built,
                        curriculumIds: mergedCurriculumIds,
                        fullData: {
                            ...(existing.fullData || {}),
                            ...(built.fullData || {}),
                            curriculumIds: mergedCurriculumIds
                        }
                    });
                });
            });

            const preceptors = Array.from(preceptorById.values()).sort((a, b) => {
                const aName = `${a.firstname || ''} ${a.lastname || ''}`.trim();
                const bName = `${b.firstname || ''} ${b.lastname || ''}`.trim();
                return aName.localeCompare(bName);
            });

            setAllData(prev => ({ ...prev, preceptors }));
        } catch (err) {
            console.error('Error fetching preceptors:', err);
            setAllData(prev => ({ ...prev, preceptors: [] }));
            setError('Failed to fetch preceptors');
        } finally {
            setLoadingPreceptors(false);
        }
    };

    // Curriculum IDs this user has schedule data for (from schedule keys, currentCurriculums, or rotationsAvailabilities)
    const getScheduleCurriculumIds = (user, type) => {
        const raw = type === 'student' ? user?.fullData : user?.fullData;
        if (!raw) return [];
        const fromExplicit = Array.isArray(user?.curriculumIds) ? user.curriculumIds.map(String) : [];
        const fromRawExplicit = Array.isArray(raw?.curriculumIds) ? raw.curriculumIds.map(String) : [];
        const fromSingle = user?.curriculumId != null ? [String(user.curriculumId)] : [];
        const fromRawSingle = raw?.curriculumId != null ? [String(raw.curriculumId)] : [];
        const fromSchedule = raw.schedule && typeof raw.schedule === 'object' && !Array.isArray(raw.schedule) ? Object.keys(raw.schedule) : [];
        const fromCurrent = Array.isArray(raw.currentCurriculums) ? raw.currentCurriculums.map(c => c && (c._id ?? c)).filter(Boolean).map(String) : [];
        const fromRa = raw.rotationsAvailabilities && typeof raw.rotationsAvailabilities === 'object' && !Array.isArray(raw.rotationsAvailabilities) ? Object.keys(raw.rotationsAvailabilities) : [];
        const set = new Set([...fromExplicit, ...fromRawExplicit, ...fromSingle, ...fromRawSingle, ...fromSchedule, ...fromCurrent, ...fromRa]);
        return Array.from(set);
    };

    const fetchSchedule = async (id, type) => {
        if (!id || !type) return;

        setFetchingSchedule(true);
        setError('');

        try {
            const endpoint = type === 'student' 
                ? `/crud/student/get_schedule_by_id/?student_id=${id}`
                : `/crud/preceptor/get_schedule_by_id/${id}`;

            const response = await axiosInstance.get(endpoint);
            
            if (type === 'preceptor') {
                // Handle preceptor curriculum-based data structure
                const scheduleData = clipScheduleMap(response.data.schedule || {});
                const currentCurriculums = response.data.currentCurriculums || [];
                
                setSchedulesByYear(scheduleData);
                setCurrentCurriculums(currentCurriculums); // Store current curriculums
                const availableYears = Object.keys(scheduleData).sort((a, b) => b - a);
                setYears(availableYears);
                
                // Set default to current curriculums if available
                if (currentCurriculums.length > 0) {
                    setCurrentYear(currentCurriculums[0]);
                    if (scheduleData[currentCurriculums[0]]) {
                        setDisplaySchedule(scheduleData[currentCurriculums[0]]);
                    }
                } else if (!currentYear && availableYears.length > 0) {
                    setCurrentYear(availableYears[0]);
                    setDisplaySchedule(scheduleData[availableYears[0]]);
                } else if (currentYear && scheduleData[currentYear]) {
                    setDisplaySchedule(scheduleData[currentYear]);
                }
            } else {
                const scheduleData = clipScheduleMap(normalizeScheduleData(response.data.schedule || {}));
                const currentCurriculumFromAPI = response.data.currentCurriculum;
                
                setSchedulesByYear(scheduleData);
                setCurrentCurriculum(currentCurriculumFromAPI || ''); // Store current curriculum
                const availableYears = Object.keys(scheduleData).sort((a, b) => b - a);
                setYears(availableYears);

                if (currentCurriculumFromAPI && scheduleData[currentCurriculumFromAPI]) {
                    setCurrentYear(currentCurriculumFromAPI);
                    setSchedule(scheduleData[currentCurriculumFromAPI]);
                    setDisplaySchedule(scheduleData[currentCurriculumFromAPI]);
                } else if (!currentYear && availableYears.length > 0) {
                    setCurrentYear(availableYears[0]);
                    setSchedule(scheduleData[availableYears[0]]);
                    setDisplaySchedule(scheduleData[availableYears[0]]);
                } else if (currentYear && scheduleData[currentYear]) {
                    setSchedule(scheduleData[currentYear]);
                    setDisplaySchedule(scheduleData[currentYear]);
                }

                if (type === 'student') {
                    const student = allData.students.find(s => s._id === id);
                    if (student) {
                        setStudentInfo(student.fullData);
                    }
                }
            }

            setIsEditing(false);
            setEditingRotation(null);
        } catch (err) {
            setSchedule([]);
            setDisplaySchedule([]);
            const errorMessage = err.response?.status === 404 
                ? `No schedule found for this ${type}` 
                : `Failed to fetch ${type}'s schedule`;
            setError(errorMessage);
        } finally {
            setFetchingSchedule(false);
        }
    };

    // Multi-schedule fetching function for enhanced multi-selection
    const fetchMultiSchedules = async (ids, type) => {
        if (!ids || ids.length === 0 || !type) return;

        setFetchingSchedule(true);
        setError('');
        const schedules = {};

        try {
            // Fetch schedules for all selected IDs
            const promises = ids.map(async (id) => {
                const endpoint = type === 'student' 
                    ? `/crud/student/get_schedule_by_id/?student_id=${id}`
                    : `/crud/preceptor/get_schedule_by_id/${id}`;

                try {
                    const response = await axiosInstance.get(endpoint);
                    const userData = (type === 'student' ? allData.students : allData.preceptors)
                        .find(user => user._id === id);
                    
                    schedules[id] = {
                        name: userData ? `${userData.firstname} ${userData.lastname}` : 'Unknown',
                        email: userData?.email || '',
                        data: clipScheduleMap(normalizeScheduleData(response.data.schedule || {})),
                        currentCurriculum: response.data.currentCurriculum || response.data.currentCurriculums?.[0] || '',
                        error: null
                    };
                } catch (err) {
                    if (import.meta.env.VITE_DEBUG_MODE === 'true') console.warn(`Failed to fetch schedule for ${type} ${id}:`, err);
                    const userData = (type === 'student' ? allData.students : allData.preceptors)
                        .find(user => user._id === id);
                    schedules[id] = {
                        name: userData ? `${userData.firstname} ${userData.lastname}` : 'Unknown',
                        email: userData?.email || '',
                        data: {},
                        currentCurriculum: '',
                        error: `No schedule found for this ${type}`
                    };
                }
            });

            await Promise.all(promises);
            setMultiSchedules(schedules);

            // Update display schedule with combined data from selected curriculums or all available data
            const combinedSchedule = [];
            Object.entries(schedules).forEach(([userId, userSchedule]) => {
                if (userSchedule.data && Object.keys(userSchedule.data).length > 0) {
                    const curriculumsToShow = selectedCurriculums.length > 0 
                        ? selectedCurriculums 
                        : Object.keys(userSchedule.data); // Show all if none selected
                    
                    curriculumsToShow.forEach(curriculumId => {
                        if (userSchedule.data[curriculumId]) {
                            userSchedule.data[curriculumId].forEach(rotation => {
                                combinedSchedule.push({
                                    ...rotation,
                                    userName: userSchedule.name,
                                    userEmail: userSchedule.email,
                                    userId: userId,
                                    curriculumId: curriculumId,
                                    curriculumName: getCurriculumDisplayName(curriculumId)
                                });
                            });
                        }
                    });
                }
            });

            setDisplaySchedule(combinedSchedule);
            setIsEditing(false);
            setEditingRotation(null);
        } catch (err) {
            setMultiSchedules({});
            setDisplaySchedule([]);
            setError(`Failed to fetch ${type} schedules`);
        } finally {
            setFetchingSchedule(false);
        }
    };

    // Fetch rotation schedule
    const fetchRotationSchedule = async (year, rotationNumber) => {
        setFetchingSchedule(true);
        setError('');
        
        try {
            const response = await axiosInstance.get(`/crud/rotation_schedule/${year}/${rotationNumber}`);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.status === 404 
                ? `No schedule found for rotation ${rotationNumber + 1} in ${getCurriculumDisplayName(year)}` 
                : `Failed to fetch rotation schedule`;
            setError(errorMessage);
            return null;
        } finally {
            setFetchingSchedule(false);
        }
    };

    // Fetch institution schedule data
    const fetchInstitutionSchedule = async (curriculumId = null) => {
        setFetchingSchedule(true);
        setError('');
        
        try {
            // Use the primary endpoint - try with fetch to bypass axios interceptor issues
            const endpoint = curriculumId 
                ? `/crud/institution/schedule?curriculum_id=${curriculumId}`
                : `/crud/institution/schedule`;
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`🔍 Fetching institution schedule from: ${endpoint}`);
            const response = await fetch(`${axiosInstance.defaults.baseURL}${endpoint}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`📊 Fetch response status: ${response.status}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`✅ Institution data received:`, data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.status === 404 
                ? `No institution schedule found for ${curriculumId ? getCurriculumDisplayName(curriculumId) : 'all years'}` 
                : `Failed to fetch institution schedule`;
            setError(errorMessage);
            return null;
        } finally {
            setFetchingSchedule(false);
        }
    };

    // Fetch speciality schedule data  
    const fetchSpecialitySchedule = async (curriculumId = null) => {
        setFetchingSchedule(true);
        setError('');
        
        try {
            // Use the primary endpoint since backend logs show it returns 200
            const endpoint = curriculumId 
                ? `/crud/speciality/schedule?curriculum_id=${curriculumId}`
                : `/crud/speciality/schedule`;
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`🔍 Fetching speciality schedule from: ${endpoint}`);
            const response = await fetch(`${axiosInstance.defaults.baseURL}${endpoint}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                }
            });
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`📊 Fetch response status: ${response.status}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log(`✅ Speciality data received:`, data);
            return data;
        } catch (err) {
            const errorMessage = err.response?.status === 404 
                ? `No speciality schedule found for ${curriculumId ? getCurriculumDisplayName(curriculumId) : 'all years'}` 
                : `Failed to fetch speciality schedule`;
            setError(errorMessage);
            return null;
        } finally {
            setFetchingSchedule(false);
        }
    };

    // Fetch curriculums from the API
    const fetchCurriculums = async () => {
        if (loadingCurriculums) return; // Prevent concurrent calls
        
        setLoadingCurriculums(true);
        try {
            const response = await axiosInstance.get('/curriculum/curriculums');
            setCurriculums(response.data || []);
            
            // Extract years from curriculums for backward compatibility
            const extractedYears = response.data.map(curriculum => curriculum._id);
            setYears(extractedYears);
        } catch (error) {
            console.error('Error fetching curriculums:', error);
            setError('Failed to fetch curriculum data');
            setCurriculums([]);
            setYears([]);
        } finally {
            setLoadingCurriculums(false);
        }
    };

    // Effect to fetch rotation schedules when selections change
    useEffect(() => {
        if (selectedType === 'rotation' && currentYear && selectedRotations.length > 0) {
            const fetchAllRotations = async () => {
                setFetchingSchedule(true);
                const rotationData = {};
                
                for (const rotation of selectedRotations) {
                    const data = await fetchRotationSchedule(currentYear, rotation);
                    if (data) {
                        rotationData[rotation] = data;
                    }
                }
                
                setRotationSchedulesByNumber(rotationData);
                setFetchingSchedule(false);
            };
            
            fetchAllRotations();
        }
    }, [selectedType, currentYear, selectedRotations]);

    // Effect to fetch institution schedules for all selected curricula
    useEffect(() => {
        if (selectedType === 'institution' && selectedYears.length > 0) {
            const fetchInstitutions = async () => {
                const merged = {};
                for (const year of selectedYears) {
                    const data = await fetchInstitutionSchedule(year);
                    if (data) merged[year] = data;
                }
                setInstitutionScheduleData(merged);
            };
            fetchInstitutions();
        } else if (selectedType === 'institution' && selectedYears.length === 0) {
            setInstitutionScheduleData({});
        }
    }, [selectedType, selectedYears]);

    // Effect to fetch speciality schedules for all selected curricula
    useEffect(() => {
        if (selectedType === 'speciality' && selectedYears.length > 0) {
            const fetchSpecialities = async () => {
                const merged = {};
                for (const year of selectedYears) {
                    const data = await fetchSpecialitySchedule(year);
                    if (data) merged[year] = data;
                }
                setSpecialityScheduleData(merged);
            };
            fetchSpecialities();
        } else if (selectedType === 'speciality' && selectedYears.length === 0) {
            setSpecialityScheduleData({});
        }
    }, [selectedType, selectedYears]);

    // Effect to fetch schedules when IDs change (supports both single and multi-selection)
    useEffect(() => {
        if ((selectedType === 'student' || selectedType === 'preceptor') && selectedIds.length > 1) {
            // Multi-selection: works with or without curriculum selection
            fetchMultiSchedules(selectedIds, selectedType);
        } else if (selectedId && selectedType && (selectedType === 'student' || selectedType === 'preceptor')) {
            // Single selection: backward compatibility
            fetchSchedule(selectedId, selectedType);
        }
    }, [selectedIds, selectedId, selectedType]);

    // Separate effect for curriculum changes in multi-selection mode
    useEffect(() => {
        if ((selectedType === 'student' || selectedType === 'preceptor') && selectedIds.length > 1 && selectedCurriculums.length > 0) {
            // Re-fetch when curriculums change in multi-selection mode
            fetchMultiSchedules(selectedIds, selectedType);
        }
    }, [selectedCurriculums]);

    // When curriculum filter is applied, restrict selected students/preceptors to those still in the filtered list
    useEffect(() => {
        if (selectedCurriculums.length === 0 || (selectedType !== 'student' && selectedType !== 'preceptor')) return;
        const list = selectedType === 'student' ? allData.students : allData.preceptors;
        if (!list || list.length === 0) return;
        const allowedIds = list.filter(u => {
            const ids = getScheduleCurriculumIds(u, selectedType);
            return ids.some(id => selectedCurriculums.includes(id) || selectedCurriculums.includes(String(id)));
        }).map(u => u._id);
        setSelectedIds(prev => {
            const next = prev.filter(id => allowedIds.includes(id) || allowedIds.includes(String(id)));
            if (next.length !== prev.length) {
                setSelectedId(cur => (next.includes(cur) || next.includes(String(cur)) ? cur : (next[0] ?? '')));
            }
            return next.length === prev.length ? prev : next;
        });
    }, [selectedCurriculums, selectedType]);

    // Always populate highlight dropdown from the selected curriculum roster,
    // not only from matrix-assigned students.
    useEffect(() => {
        if (selectedType === 'institution' || selectedType === 'speciality') {
            const students = extractStudentsByCurriculum(selectedYears);
            setAvailableStudents(students);

            if (selectedStudent && !students.some((student) => String(student.id) === String(selectedStudent))) {
                setSelectedStudent(null);
            }
            setMatrixDirtyByCurriculum({});
        } else {
            setAvailableStudents([]);
            setSelectedStudent(null);
            setMatrixDirtyByCurriculum({});
        }
    }, [selectedType, selectedYears, allData.students, selectedStudent]);

    // Apply date range filter to displayed schedule (operates on what shows on screen)
    const displayScheduleFiltered = React.useMemo(() => {
        const hasCurriculumSelection = selectedCurriculums.length > 0 || selectedYears.length > 0;
        if (!hasCurriculumSelection) return displaySchedule;
        if (!startDate && !endDate) return displaySchedule;
        const start = parseDate(startDate);
        const end = parseDate(endDate);
        if (!start && !end) return displaySchedule;
        return displaySchedule.filter(row => {
            const rowStart = parseDate(row.rotationStartDate ?? row.start_date);
            const rowEnd = parseDate(row.rotationEndDate ?? row.end_date);
            if (!rowStart || !rowEnd) return true;
            return rowStart <= (end || rowEnd) && rowEnd >= (start || rowStart);
        });
    }, [displaySchedule, startDate, endDate, selectedCurriculums, selectedYears]);

    // Render schedule table for student/preceptor (supports multi-user mode)
    const renderScheduleTable = (data, type, isMultiUser = false) => {
        if (!data || data.length === 0) return null;

        return (
            <div className="modern-schedule-table-container">
                {!isMultiUser && (
                    <div className="modern-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3>Curriculum: {getCurriculumDisplayName(selectedYears[0] || currentYear || new Date().getFullYear())}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        {type === 'student' && selectedId && displayScheduleFiltered.length > 0 && (
                            <>
                                {!isEditing ? (
                                    <button 
                                        className="modern-button primary"
                                        onClick={handleEditMode}
                                        disabled={fetchingSchedule}
                                        style={{
                                            background: '#fff',
                                            color: '#2b4663',
                                            border: '2px solid #2b4663',
                                            borderRadius: 12,
                                            padding: '14px 32px',
                                            fontWeight: 600,
                                            fontSize: 16,
                                            letterSpacing: 0.5,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            outline: 'none',
                                            boxShadow: '0 10px 24px rgba(19, 75, 95, 0.12)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.background = 'linear-gradient(135deg, #e4f4f7 0%, #ffffff 100%)';
                                            e.target.style.boxShadow = '0 14px 28px rgba(19, 75, 95, 0.16)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.background = 'linear-gradient(135deg, #f7fbfc 0%, #edf6f8 100%)';
                                            e.target.style.boxShadow = '0 10px 24px rgba(19, 75, 95, 0.12)';
                                        }}
                                    >
                                        <span>✏️</span>
                                        Edit Schedule
                                    </button>
                                ) : (
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button 
                                            className="modern-button success"
                                            onClick={handleSaveChanges}
                                            style={{
                                                background: '#fff',
                                                color: '#16a34a',
                                                border: '2px solid #16a34a',
                                                borderRadius: 12,
                                                padding: '10px 20px',
                                                fontWeight: 600,
                                                fontSize: 14,
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                outline: 'none',
                                                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.1)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 6px 20px rgba(22, 163, 74, 0.2)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 2px 8px rgba(22, 163, 74, 0.1)';
                                            }}
                                        >
                                            💾 Save
                                        </button>
                                        <button 
                                            className="modern-button secondary"
                                            onClick={handleCancelEditing}
                                            style={{
                                                background: '#fff',
                                                color: '#dc2626',
                                                border: '2px solid #dc2626',
                                                borderRadius: 12,
                                                padding: '10px 20px',
                                                fontWeight: 600,
                                                fontSize: 14,
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                outline: 'none',
                                                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.1)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.2)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.1)';
                                            }}
                                        >
                                            ❌ Cancel
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                        <ColumnSelector 
                            availableColumns={availableColumns[type] || []}
                            visibleColumns={visibleColumns[type] || []}
                            onColumnToggle={handleColumnToggle}
                            onExportTable={() => handleExport('CSV')}
                        />
                        </div>
                    </div>
                )}

                <table className="modern-schedule-table">
                    <thead>
                        <tr key={`header-${(visibleColumns[type] || []).join('-')}`} style={{
                            background: 'linear-gradient(90deg, #2b4663 0%, #3c5a7a 25%, #4a90e2 75%, #5ba3f5 100%) !important',
                            backgroundSize: '100% 100% !important'
                        }}>
                            {type === 'student' && (
                                <>
                                    {isMultiUser && visibleColumns.student.includes('userName') && (
                                        <SortableTableHeader label="Student" columnKey="userName" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {isMultiUser && visibleColumns.student.includes('curriculumName') && (
                                        <SortableTableHeader label="Curriculum" columnKey="curriculumName" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('rotation') && (
                                        <SortableTableHeader label="Rotation" columnKey="rotation" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('date_range') && (
                                        <SortableTableHeader label="Date Range" columnKey="date_range" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('primary_preceptor') && (
                                        <SortableTableHeader label="Primary Preceptor" columnKey="primary_preceptor" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('co_preceptor') && (
                                        <SortableTableHeader label="Co-Preceptor" columnKey="co_preceptor" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('email') && (
                                        <SortableTableHeader label="Email" columnKey="email" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('speciality') && (
                                        <SortableTableHeader label="Experience" columnKey="speciality" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {visibleColumns.student.includes('institution') && (
                                        <SortableTableHeader label="Institution" columnKey="institution" sortState={sortState.student} onSort={col => handleSort('student', col)} />
                                    )}
                                    {isEditing && <th>Actions</th>}
                                </>
                            )}
                            {type === 'preceptor' && (
                                <>
                                    {isMultiUser && visibleColumns.preceptor.includes('userName') && (
                                        <SortableTableHeader label="Preceptor" columnKey="userName" sortState={sortState.preceptor} onSort={col => handleSort('preceptor', col)} />
                                    )}
                                    {isMultiUser && visibleColumns.preceptor.includes('curriculumName') && (
                                        <SortableTableHeader label="Curriculum" columnKey="curriculumName" sortState={sortState.preceptor} onSort={col => handleSort('preceptor', col)} />
                                    )}
                                    {visibleColumns.preceptor.includes('rotation') && (
                                        <SortableTableHeader label="Rotation" columnKey="rotation" sortState={sortState.preceptor} onSort={col => handleSort('preceptor', col)} />
                                    )}
                                    {visibleColumns.preceptor.includes('dateRange') && (
                                        <SortableTableHeader label="Date Range" columnKey="dateRange" sortState={sortState.preceptor} onSort={col => handleSort('preceptor', col)} />
                                    )}
                                    {visibleColumns.preceptor.includes('students') && (
                                        <SortableTableHeader label="Students" columnKey="students" sortState={sortState.preceptor} onSort={col => handleSort('preceptor', col)} />
                                    )}
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {type === 'student' && getSortedData(applyAdvancedSearch(data, advancedSearch, 'student'), 'student').map((rotation, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                {isMultiUser && visibleColumns.student.includes('userName') && (
                                    <td style={{ fontWeight: '600', color: '#2563eb' }}>
                                        {rotation.userName || 'Unknown Student'}
                                    </td>
                                )}
                                {isMultiUser && visibleColumns.student.includes('curriculumName') && (
                                    <td style={{ fontWeight: '500', color: '#7c3aed' }}>
                                        {rotation.curriculumName || 'Unknown Curriculum'}
                                    </td>
                                )}
                                {visibleColumns.student.includes('rotation') && <td>Rotation {(rotation.rotation !== undefined ? rotation.rotation : index) + 1}</td>}
                                {visibleColumns.student.includes('date_range') && (
                                    <td>
                                        {rotation.rotationStartDate && rotation.rotationEndDate ?
                                            `${formatDate(parseMongoDate(rotation.rotationStartDate))} - ${formatDate(parseMongoDate(rotation.rotationEndDate))}` :
                                            '—'
                                        }
                                    </td>
                                )}
                                {visibleColumns.student.includes('primary_preceptor') && (
                                    <td>
                                        {(() => {
                                            const pr = rotation.preceptor;
                                            const fn = pr?.firstname != null && String(pr.firstname) !== 'undefined' ? String(pr.firstname).trim() : '';
                                            const ln = pr?.lastname != null && String(pr.lastname) !== 'undefined' ? String(pr.lastname).trim() : '';
                                            const name = `${fn} ${ln}`.trim();
                                            return name ? `Dr. ${name}` : <OffBadge />;
                                        })()}
                                    </td>
                                )}
                                {visibleColumns.student.includes('co_preceptor') && (
                                    <td>
                                        {(() => {
                                            const co = rotation.copreceptor;
                                            const fn = co?.firstname != null && String(co.firstname) !== 'undefined' ? String(co.firstname).trim() : '';
                                            const ln = co?.lastname != null && String(co.lastname) !== 'undefined' ? String(co.lastname).trim() : '';
                                            const name = `${fn} ${ln}`.trim();
                                            return name ? `Dr. ${name}` : <OffBadge />;
                                        })()}
                                    </td>
                                )}
                                {visibleColumns.student.includes('email') && (
                                    <td>{rotation.preceptor?.email || 'N/A'}</td>
                                )}
                                {visibleColumns.student.includes('speciality') && (
                                    <td>{getRotationSpecialityName(rotation) || 'N/A'}</td>
                                )}
                                {visibleColumns.student.includes('institution') && (
                                    <td>{rotation.institution?.name || 'N/A'}</td>
                                )}
                                {isEditing && (
                                    <td className="action-buttons">
                                        <button 
                                            className="modern-button secondary"
                                            style={{ 
                                                fontSize: '0.8rem', 
                                                padding: '6px 12px', 
                                                marginBottom: '4px',
                                                background: 'var(--white)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                color: 'var(--text-primary)',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                if (window.editingState) {
                                                    const studentKey = getEditingStateKey(rotation);
                                                    if (studentKey && window.editingState[studentKey]) {
                                                        window.editingState[studentKey].changes.push({
                                                            type: 'preceptor_change',
                                                            rotationIndex: index,
                                                            timestamp: new Date().toISOString()
                                                        });
                                                    }
                                                }
                                                openPreceptorAssignModal(rotation, index, 'preceptor');
                                            }}
                                        >
                                            Change Preceptor
                                        </button>
                                        <button 
                                            className="modern-button secondary"
                                            style={{ 
                                                fontSize: '0.8rem', 
                                                padding: '6px 12px', 
                                                marginBottom: '4px',
                                                background: 'var(--white)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                color: 'var(--text-primary)',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => {
                                                if (window.editingState) {
                                                    const studentKey = getEditingStateKey(rotation);
                                                    if (studentKey && window.editingState[studentKey]) {
                                                        window.editingState[studentKey].changes.push({
                                                            type: 'copreceptor_change',
                                                            rotationIndex: index,
                                                            timestamp: new Date().toISOString()
                                                        });
                                                    }
                                                }
                                                openPreceptorAssignModal(rotation, index, 'copreceptor');
                                            }}
                                        >
                                            {rotation.copreceptor ? 'Change Co-Preceptor' : 'Add Co-Preceptor'}
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {type === 'preceptor' && getSortedData(applyAdvancedSearch(data.filter(rotation => rotation && typeof rotation === 'object' && rotation.rotation !== undefined), advancedSearch, 'preceptor'), 'preceptor').map((rotationData, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                {isMultiUser && visibleColumns.preceptor.includes('userName') && (
                                    <td style={{ fontWeight: '600', color: '#2563eb' }}>
                                        {rotationData.userName || 'Unknown Preceptor'}
                                    </td>
                                )}
                                {isMultiUser && visibleColumns.preceptor.includes('curriculumName') && (
                                    <td style={{ fontWeight: '500', color: '#7c3aed' }}>
                                        {rotationData.curriculumName || 'Unknown Curriculum'}
                                    </td>
                                )}
                                {visibleColumns.preceptor.includes('rotation') && (
                                    <td>Rotation {rotationData.rotation + 1}</td>
                                )}
                                {visibleColumns.preceptor.includes('dateRange') && (
                                    <td>
                                        {rotationData.rotationStartDate && rotationData.rotationEndDate ? 
                                            `${formatDate(rotationData.rotationStartDate)} - ${formatDate(rotationData.rotationEndDate)}` : 
                                            'Date range not available'}
                                    </td>
                                )}
                                {visibleColumns.preceptor.includes('students') && (
                                    <td>
                                        {Array.isArray(rotationData.students) && rotationData.students.length > 0 ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {rotationData.students.map((student, sIndex) => (
                                                    <span
                                                        key={`${student.name || 'student'}-${sIndex}`}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            padding: '4px 10px',
                                                            borderRadius: '999px',
                                                            border: '1px solid #cbd5e1',
                                                            background: '#f8fafc',
                                                            color: '#334155',
                                                            fontWeight: 500,
                                                            fontSize: '0.82rem'
                                                        }}
                                                        title={student.email || ''}
                                                    >
                                                        {student.name}
                                                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                            {student.isCoprecepted ? 'Co' : 'Pr'}
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <OffBadge />
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Key for editing state: same logic as handleEditMode (supports single-student when userName/curriculumName missing)
    const getEditingStateKey = (rotation) => {
        const sid = rotation.studentId || selectedId;
        const cid = rotation.curriculumId || currentYear;
        if (!sid || !cid) return null;
        return rotation.userName && rotation.curriculumName
            ? `${rotation.userName}-${rotation.curriculumName}`
            : `${sid}-${cid}`;
    };

    // Helper function to check if a rotation has been modified
    const isRotationModified = (rotation, index) => {
        if (!window.editingState) return false;
        const studentKey = getEditingStateKey(rotation);
        if (!studentKey) return false;
        const editState = window.editingState[studentKey];
        return editState && editState.changes.some(change => change.rotationIndex === index);
    };

    // Enhanced edit mode handlers with proper state tracking
    const handleEditMode = () => {
        setIsEditing(true);
        setNewSchedule(Array(displaySchedule.length).fill([]));
        setPendingCoPreceptorChanges({});
        
        // Track original state for each student and curriculum (support single-student: use studentId/curriculumId when userName/curriculumName missing)
        const editingState = {};
        displaySchedule.forEach((rotation) => {
            const sid = rotation.studentId || selectedId;
            const cid = rotation.curriculumId || currentYear;
            if (!sid || !cid) return;
            const key = rotation.userName && rotation.curriculumName
                ? `${rotation.userName}-${rotation.curriculumName}`
                : `${sid}-${cid}`;
            if (!editingState[key]) {
                const sameStudent = (r) => (r.studentId || selectedId) === sid;
                const sameCurriculum = (r) => (r.curriculumId || currentYear) === cid;
                editingState[key] = {
                    studentId: sid,
                    curriculumId: cid,
                    originalSchedule: [...displaySchedule.filter(r => sameStudent(r) && sameCurriculum(r))],
                    changes: []
                };
            }
        });
        
        // Store editing state for tracking changes
        window.editingState = editingState;
    };

    const handleCancelEditing = () => {
        setIsEditing(false);
        setEditingRotation(null);
        setAddingCoPreceptor(null);
        setCoPreceptors({});
        setDisplaySchedule(schedule);
        setFilteredPreceptors([]);
        setPendingCoPreceptorChanges({});
        setPreceptorAssignModal(prev => ({ ...prev, open: false }));
        
        // Clean up editing state
        window.editingState = null;
        
        // Restore original schedule data
        if (selectedIds.length > 1) {
            // For multi-user mode, refresh the multi-schedule data
            fetchMultiSchedules(selectedIds, selectedType);
        } else if (selectedId) {
            // For single user mode, refresh single schedule
            fetchSchedule(selectedId, selectedType);
        }
    };

    const openPreceptorAssignModal = async (rotation, index, type) => {
        const curriculumId = rotation.curriculumId || currentYear || '';
        const curriculumName = rotation.curriculumName || getCurriculumDisplayName(curriculumId);
        const studentId = rotation.studentId || selectedId;
        const primaryPreceptorId = rotation.preceptor?._id || null;
        const currentAssignedPreceptorId =
            type === 'copreceptor' ? (rotation.copreceptor?._id || null) : (rotation.preceptor?._id || null);
        const rotationNumber = rotation.rotation !== undefined ? rotation.rotation : index;
        setPreceptorAssignModal({
            open: true,
            type,
            rotationIndex: index,
            rotationNumber,
            curriculumId,
            curriculumName,
            studentId,
            primaryPreceptorId: type === 'copreceptor' ? primaryPreceptorId : null,
            currentAssignedPreceptorId,
            currentAssignedEntityId: null,
            list: [],
            loading: true,
            matchBy: null,
            entityNames: {},
            savingAvailabilityFor: null,
            editingAvailabilityFor: null,
            editAvailabilityDraft: [],
            search: '',
            filterSpecialityId: '',
            filterInstitutionId: ''
        });
        try {
            const rotation1Based = (rotationNumber != null ? rotationNumber : 0) + 1;
            if (type === 'preceptor' && selectedType === 'student' && curriculumId) {
                const res = await axiosInstance.get(`/curriculum/${encodeURIComponent(curriculumId)}/preceptors`);
                const source = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data?.preceptors)
                        ? res.data.preceptors
                        : [];
                // Which entity type this curriculum's rank matches preceptors by — decides
                // whether preceptors are grouped/filtered by experience or by institution.
                const matchBy = res.data?.matchBy === 'institution' ? 'institution' : 'experience';
                const entityNames = (res.data?.entityNames && typeof res.data.entityNames === 'object') ? res.data.entityNames : {};

                // Expand each preceptor into ONE ROW PER MATCHING ENTITY they hold, instead
                // of collapsing to a single legacy speciality/institution — a preceptor with
                // 3 experiences must be selectable/visible under each of the 3, each with its
                // own availability for this rotation.
                const expandedRows = [];
                source.forEach((raw) => {
                    const built = buildCurriculumPreceptorRecord(raw, curriculumId);
                    if (!built?._id) return;
                    const preceptorId = String(built._id);
                    const availabilitiesByEntity = (raw?.availabilitiesBySpeciality && typeof raw.availabilitiesBySpeciality === 'object')
                        ? raw.availabilitiesBySpeciality
                        : {};
                    const legacyIds = matchBy === 'institution'
                        ? (Array.isArray(raw?.institutionIds) && raw.institutionIds.length > 0
                            ? raw.institutionIds
                            : (raw?.institutionId ? [raw.institutionId] : []))
                        : (Array.isArray(raw?.specialityIds) && raw.specialityIds.length > 0
                            ? raw.specialityIds
                            : (raw?.specialityId ? [raw.specialityId] : []));
                    const matchingEntityIds = (Array.isArray(raw?.matchingEntityIds) && raw.matchingEntityIds.length > 0
                        ? raw.matchingEntityIds
                        : legacyIds
                    ).map(String);

                    (matchingEntityIds.length > 0 ? matchingEntityIds : ['']).forEach((entityId) => {
                        const availArray = entityId ? availabilitiesByEntity[entityId] : null;
                        const availability = Array.isArray(availArray) ? availArray[rotationNumber] : undefined;
                        const entityName = entityId ? (entityNames[entityId] || entityId) : null;
                        expandedRows.push({
                            ...built,
                            _id: preceptorId,
                            preceptorId,
                            rowKey: `${preceptorId}::${entityId || '_none'}`,
                            entityId,
                            specialityId: matchBy === 'experience' ? entityId : (built.specialityId || ''),
                            institutionId: matchBy === 'institution' ? entityId : (built.institutionId || ''),
                            speciality: matchBy === 'experience' && entityId ? { _id: entityId, name: entityName } : null,
                            institution: matchBy === 'institution' && entityId ? { _id: entityId, name: entityName } : null,
                            availability,
                            rotationsAvailabilities: { [String(curriculumId)]: availabilitiesByEntity }
                        });
                    });
                });

                // The entity actually stored on this rotation slot right now — used to mark
                // exactly one row (not every row for that preceptor) as "Current".
                const currentAssignedEntityId = matchBy === 'institution'
                    ? (rotation?.preceptor?.institutionId ? String(rotation.preceptor.institutionId) : null)
                    : (rotation?.preceptor?.specialityId ? String(rotation.preceptor.specialityId) : null);

                const currentAssignedRowKey = rotation?.preceptor?._id
                    ? `${String(rotation.preceptor._id)}::${currentAssignedEntityId || '_none'}`
                    : null;

                const currentAssigned = rotation?.preceptor?._id
                    ? {
                        _id: String(rotation.preceptor._id),
                        preceptorId: String(rotation.preceptor._id),
                        rowKey: currentAssignedRowKey,
                        entityId: currentAssignedEntityId,
                        firstname: rotation.preceptor.firstname || '',
                        lastname: rotation.preceptor.lastname || '',
                        email: rotation.preceptor.email || '',
                        specialityId: rotation.preceptor.specialityId || '',
                        institutionId: rotation.preceptor.institutionId || '',
                        speciality: rotation.speciality || null,
                        institution: rotation.institution || null,
                        curriculumIds: curriculumId ? [String(curriculumId)] : [],
                        rotationsAvailabilities: {},
                        availability: rotation.preceptor.availability,
                        __isCurrentAssignedOnly: true
                    }
                    : null;

                const byRowKey = new Map();
                expandedRows.forEach((row) => {
                    if (!row?.rowKey) return;
                    byRowKey.set(row.rowKey, row);
                });
                if (currentAssigned && !byRowKey.has(currentAssigned.rowKey)) {
                    byRowKey.set(currentAssigned.rowKey, currentAssigned);
                }

                const list = Array.from(byRowKey.values()).sort((a, b) => {
                    const aName = `${a.firstname || ''} ${a.lastname || ''}`.trim();
                    const bName = `${b.firstname || ''} ${b.lastname || ''}`.trim();
                    if (aName !== bName) return aName.localeCompare(bName);
                    return (a.entityId || '').localeCompare(b.entityId || '');
                });

                setPreceptorAssignModal(prev => ({
                    ...prev,
                    list,
                    matchBy,
                    entityNames,
                    currentAssignedEntityId,
                    loading: false
                }));
            } else if (type === 'preceptor') {
                const res = await axiosInstance.get('/crud/get_available_preceptors/', {
                    params: { curriculum_id: curriculumId, rotation: rotation1Based }
                });
                const list = Array.isArray(res.data) ? res.data : [];
                setPreceptorAssignModal(prev => ({ ...prev, list, loading: false }));
            } else {
                if (!primaryPreceptorId) {
                    showError('Select a primary preceptor first');
                    setPreceptorAssignModal(prev => ({ ...prev, open: false }));
                    return;
                }
                const res = await axiosInstance.get('/crud/available_copreceptors', {
                    params: {
                        curriculum_id: curriculumId,
                        primary_preceptor_id: primaryPreceptorId,
                        rotation: rotation1Based
                    }
                });
                const list = Array.isArray(res.data) ? res.data : [];
                setPreceptorAssignModal(prev => ({ ...prev, list, loading: false }));
            }
        } catch (err) {
            showError(err.response?.data?.detail || (type === 'preceptor' ? 'Failed to load preceptors' : 'Failed to load co-preceptors'));
            setPreceptorAssignModal(prev => ({ ...prev, open: false, loading: false }));
        }
    };

    const closePreceptorAssignModal = () => {
        setPreceptorAssignModal(prev => ({
            ...prev,
            open: false,
            savingAvailabilityFor: null,
            editingAvailabilityFor: null,
            currentAssignedPreceptorId: null,
            currentAssignedEntityId: null,
            search: '',
            filterSpecialityId: '',
            filterInstitutionId: ''
        }));
    };

    const getRotationAvailabilityLabel = (val) => {
        const n = typeof val === 'number' && !Number.isNaN(val) ? val : parseInt(val, 10);
        if (n === 0) return { text: 'Unavailable', class: 'avail-unavailable' };
        if (n === 1) return { text: '1 student', class: 'avail-single' };
        if (n === 2) return { text: '2 students', class: 'avail-double' };
        if (n > 0 && n < 1000) return { text: `${n} students`, class: 'avail-double' };
        return { text: '—', class: '' };
    };

    // Normalize availability to 8-element number array (handles array or object with keys 0-7). Preserves values > 2 for display/editing.
    const normalizeAvailabilityList = (list) => {
        const parse = (v) => Math.max(0, Math.min(99, parseInt(v, 10) || 0));
        if (Array.isArray(list) && list.length >= 8) {
            return list.slice(0, 8).map(parse);
        }
        if (list && typeof list === 'object' && !Array.isArray(list)) {
            const arr = [];
            for (let i = 0; i < 8; i++) {
                const v = list[i] ?? list[String(i)];
                arr.push(parse(v));
            }
            return arr;
        }
        return null;
    };

    // `rowKey` identifies one preceptor+entity row (`${preceptorId}::${entityId}`) —
    // a preceptor with several experiences/institutions has one row per entity, and
    // editing must target that ONE entity's availability, not the preceptor's first.
    const startEditingAvailability = async (rowKey) => {
        const { curriculumId, list: modalList } = preceptorAssignModal;
        const row = modalList?.find(p => p.rowKey === rowKey);
        if (!row) return;
        // Prefer existing data from modal list (same source as the badge display) so edit shows actual values
        const entityAvail = row.rotationsAvailabilities && typeof row.rotationsAvailabilities === 'object'
            ? row.rotationsAvailabilities[String(curriculumId)]
            : null;
        const fromList = entityAvail && typeof entityAvail === 'object' && row.entityId
            ? entityAvail[row.entityId]
            : null;
        let draft = normalizeAvailabilityList(fromList);
        if (draft) {
            setPreceptorAssignModal(prev => ({ ...prev, editingAvailabilityFor: rowKey, editAvailabilityDraft: draft }));
            return;
        }
        try {
            const res = await axiosInstance.get('/crud/get_preceptor_availability', { params: { preceptor_id: row.preceptorId } });
            const raw = res.data;
            // API may return { rotationsAvailabilities: { curriculumId: { entityId: [...] } } } or { data: { rotationsAvailabilities: {...} } }
            const ra = (raw?.data?.rotationsAvailabilities && typeof raw.data.rotationsAvailabilities === 'object')
                ? raw.data.rotationsAvailabilities
                : (raw?.rotationsAvailabilities && typeof raw.rotationsAvailabilities === 'object')
                    ? raw.rotationsAvailabilities
                    : raw && typeof raw === 'object' && !Array.isArray(raw)
                        ? raw
                        : null;
            const curriculumAvail = curriculumId && ra && typeof ra === 'object' && !Array.isArray(ra)
                ? (ra[String(curriculumId)] ?? ra[curriculumId])
                : null;
            const list = curriculumAvail && typeof curriculumAvail === 'object' && !Array.isArray(curriculumAvail) && row.entityId
                ? curriculumAvail[row.entityId]
                : Array.isArray(curriculumAvail) && curriculumAvail.length >= 8 ? curriculumAvail : null;
            draft = normalizeAvailabilityList(list) || Array(8).fill(0);
            setPreceptorAssignModal(prev => ({ ...prev, editingAvailabilityFor: rowKey, editAvailabilityDraft: draft }));
        } catch (err) {
            showError(err.response?.data?.detail || 'Failed to load availability');
        }
    };

    const saveAvailabilityFromModal = async () => {
        const { editingAvailabilityFor, editAvailabilityDraft, curriculumId, list, rotationNumber } = preceptorAssignModal;
        if (!editingAvailabilityFor || !curriculumId) return;
        const rn = rotationNumber != null ? rotationNumber : 0;
        const editedValue = Math.max(0, Math.min(99, parseInt(editAvailabilityDraft[rn], 10) || 0));
        const row = list.find(p => p.rowKey === editingAvailabilityFor);
        const entityId = row?.entityId || row?.specialityId || row?.institutionId;
        if (!entityId) {
            showError('This preceptor does not have a resolvable experience/institution for availability updates.');
            return;
        }
        const entityAvail = row?.rotationsAvailabilities && typeof row.rotationsAvailabilities === 'object'
            ? row.rotationsAvailabilities[String(curriculumId)]
            : null;
        const existing = entityAvail && typeof entityAvail === 'object' ? entityAvail[entityId] : null;
        const parseSlot = (v) => Math.max(0, Math.min(99, parseInt(v, 10) || 0));
        const baseArray = Array.isArray(existing) && existing.length === 8 ? existing.map(parseSlot) : Array(8).fill(0);
        const payload = [...baseArray];
        payload[rn] = editedValue;
        try {
            setPreceptorAssignModal(prev => ({ ...prev, savingAvailabilityFor: editingAvailabilityFor }));
            await axiosInstance.put('/crud/admin/preceptor/availability', null, {
                params: {
                    preceptor_id: row.preceptorId,
                    curriculum_id: curriculumId,
                    entity_id: entityId,
                    new_availability: JSON.stringify(payload)
                }
            });
            setPreceptorAssignModal(prev => ({
                ...prev,
                savingAvailabilityFor: null,
                editingAvailabilityFor: null,
                editAvailabilityDraft: [],
                list: prev.list.map(p => {
                    if (p.rowKey !== editingAvailabilityFor) return p;
                    const ra = p.rotationsAvailabilities && typeof p.rotationsAvailabilities === 'object' ? { ...p.rotationsAvailabilities } : {};
                    const curr = ra[String(curriculumId)] && typeof ra[String(curriculumId)] === 'object' ? { ...ra[String(curriculumId)] } : {};
                    curr[entityId] = payload;
                    ra[String(curriculumId)] = curr;
                    return { ...p, rotationsAvailabilities: ra, availability: payload[rn] };
                })
            }));
        } catch (err) {
            setPreceptorAssignModal(prev => ({ ...prev, savingAvailabilityFor: null }));
            showError(err.response?.data?.detail || 'Failed to update availability');
        }
    };

    const assignPreceptorToRow = (preceptor) => {
        const { type, rotationNumber, curriculumId, studentId } = preceptorAssignModal;
        const rn = rotationNumber != null ? rotationNumber : 0;
        const cid = curriculumId || currentYear;
        const sid = studentId || selectedId;
        // Find the row in displaySchedule by identity (rotation slot + curriculum + student), not by table index.
        // Table index is from sorted/filtered data and does not match displaySchedule indices.
        const targetIndex = displaySchedule.findIndex((r) => {
            const rRotation = r.rotation !== undefined ? r.rotation : -1;
            const rCurriculum = r.curriculumId || currentYear;
            const rStudent = r.studentId || selectedId;
            if (selectedIds.length <= 1) return rRotation === rn;
            return rRotation === rn && String(rCurriculum) === String(cid) && String(rStudent) === String(sid);
        });
        if (targetIndex === -1) {
            showError('Could not find the rotation to update. Please cancel and try again.');
            return;
        }
        const updated = [...displaySchedule];
        const row = updated[targetIndex];
        if (!row) return;
        if (type === 'preceptor') {
            if (!preceptor || !preceptor._id) {
                updated[targetIndex] = {
                    ...row,
                    preceptor: null,
                    copreceptor: null
                };
                setDisplaySchedule(updated);
                closePreceptorAssignModal();
                setEditingRotation(null);
                setAddingCoPreceptor(null);
                return;
            }
            updated[targetIndex] = {
                ...row,
                preceptor: {
                    _id: preceptor._id,
                    firstname: preceptor.firstname,
                    lastname: preceptor.lastname,
                    email: preceptor.email,
                    specialityId: preceptor.specialityId,
                    institutionId: preceptor.institutionId
                },
                speciality: preceptor.speciality ? (typeof preceptor.speciality === 'object' ? preceptor.speciality : { name: preceptor.speciality }) : row.speciality,
                institution: preceptor.institution ? (typeof preceptor.institution === 'object' ? preceptor.institution : { name: preceptor.institution }) : row.institution
            };
        } else {
            if (!preceptor || !preceptor._id) {
                updated[targetIndex] = {
                    ...row,
                    copreceptor: null
                };
                setDisplaySchedule(updated);
                closePreceptorAssignModal();
                setEditingRotation(null);
                setAddingCoPreceptor(null);
                return;
            }
            updated[targetIndex] = {
                ...row,
                copreceptor: {
                    _id: preceptor._id,
                    firstname: preceptor.firstname,
                    lastname: preceptor.lastname
                }
            };
        }
        setDisplaySchedule(updated);
        closePreceptorAssignModal();
        setEditingRotation(null);
        setAddingCoPreceptor(null);
    };

    const handleSaveChanges = async () => {
        try {
            const editingState = window.editingState || {};
            
            if (Object.keys(editingState).length === 0) {
                showError('No editing state found. Please click "Edit Schedule" first, then make your changes and save.');
                return;
            }

            const apiCalls = [];
            const studentUpdates = {};

            // Group changes by student and curriculum. Use the same id the API expects: single-student => selectedId (used to fetch); multi => rotation.userId (from combined schedule).
            const isSingleStudent = selectedIds.length <= 1;
            displaySchedule.forEach((rotation) => {
                const studentIdForApi = isSingleStudent ? selectedId : (rotation.userId || rotation.studentId || selectedId);
                const curriculumId = rotation.curriculumId || currentYear;
                if (!studentIdForApi || !curriculumId) return;

                if (!studentUpdates[studentIdForApi]) {
                    studentUpdates[studentIdForApi] = {};
                }
                if (!studentUpdates[studentIdForApi][curriculumId]) {
                    studentUpdates[studentIdForApi][curriculumId] = [];
                }
                const scheduleEntry = {
                    preceptor_id: rotation.preceptor?._id != null ? String(rotation.preceptor._id) : null,
                    speciality_id: rotation.preceptor?.specialityId != null ? String(rotation.preceptor.specialityId) : null,
                    institution_id: rotation.preceptor?.institutionId != null ? String(rotation.preceptor.institutionId) : '',
                    copreceptor_id: rotation.copreceptor?._id != null ? String(rotation.copreceptor._id) : null
                };
                studentUpdates[studentIdForApi][curriculumId].push(scheduleEntry);
            });

            // Create API calls for each student-curriculum combination. Body must be JSON: { schedule: [ ... ] } or array.
            Object.keys(studentUpdates).forEach(studentId => {
                Object.keys(studentUpdates[studentId]).forEach(curriculumId => {
                    const scheduleArray = studentUpdates[studentId][curriculumId];
                    const url = `/crud/update_student_schedule/${studentId}/${curriculumId}`;
                    const body = { schedule: scheduleArray };
                    apiCalls.push(
                        axiosInstance.put(url, body, { headers: { 'Content-Type': 'application/json' } }).then(() => ({
                            success: true,
                            studentId,
                            curriculumId,
                            message: `Updated schedule for student ${studentId} in curriculum ${curriculumId}`
                        })).catch(error => ({
                            success: false,
                            studentId,
                            curriculumId,
                            error: error.response?.data?.message || error.message
                        }))
                    );
                });
            });

            // Execute all API calls
            const results = await Promise.all(apiCalls);
            
            // Process results
            const successes = results.filter(r => r.success);
            const failures = results.filter(r => !r.success);
            
            if (failures.length === 0) {
                showSuccess(`Successfully updated schedules for ${successes.length} student-curriculum combination(s)`);
                setIsEditing(false);
                setEditingRotation(null);
                setPendingCoPreceptorChanges({});
                setFilteredPreceptors([]);
                window.editingState = null;
                
                // Refresh schedule data based on current mode
                if (selectedIds.length > 1) {
                    await fetchMultiSchedules(selectedIds, selectedType);
                } else if (selectedId) {
                    await fetchSchedule(selectedId, selectedType);
                }
            } else {
                const errorMessages = failures.map(f => `Student ${f.studentId}: ${f.error}`).join('\n');
                showError(`Some updates failed:\n${errorMessages}`);
            }
        } catch (err) {
            let errorMessage = 'Failed to update schedule';
            if (err.response?.data) {
                const responseError = err.response.data;
                errorMessage = typeof responseError === 'string' ? responseError : 
                              responseError.detail || responseError.msg || JSON.stringify(responseError);
            }
            showError(errorMessage);
        }
    };

    const isCurriculumMultiSelect = selectedType === 'student' || selectedType === 'preceptor';
    const curriculumSelectValue = isCurriculumMultiSelect
        ? selectedCurriculums.map(id => ({ value: id, label: getCurriculumDisplayName(id) }))
        : (selectedYears[0]
            ? { value: selectedYears[0], label: getCurriculumDisplayName(selectedYears[0]) }
            : null);

    const hasCurriculumForDateFilter = selectedCurriculums.length > 0 || selectedYears.length > 0;
    const hasAnyFilters = Boolean(
        startDate ||
        endDate ||
        selectedYears.length > 0 ||
        selectedCurriculums.length > 0 ||
        selectedRotations.length > 0 ||
        showCurrentOnly ||
        selectedIds.length > 0 ||
        selectedId
    );
    const selectedUsersCount = selectedIds.length || (selectedId ? 1 : 0);
    const visibleRowsCount = Array.isArray(displayScheduleFiltered) ? displayScheduleFiltered.length : 0;
    const selectedCurriculumCount = isCurriculumMultiSelect ? selectedCurriculums.length : selectedYears.length;
    const loadedCurriculumCount = curriculums.length;

    return (
        <div className="view-admin-schedules-page vs-shell" style={viewAdminSchedulesPageStyle}>
            {/* Header: same presence as Curriculum Management */}
            <div className="view-admin-schedules-header">
                <div className="view-admin-schedules-header-inner">
                    <div className="view-admin-schedules-header-left">
                        <div className="view-admin-schedules-header-icon">
                            <span aria-hidden>📅</span>
                        </div>
                        <div>
                            <h1 className="view-admin-schedules-title">{headerTitle}</h1>
                            <p className="view-admin-schedules-subtitle">{getScheduleSubtitle(selectedType || scheduleType)}</p>
                        </div>
                    </div>
                    <div className="view-admin-schedules-header-actions">
                        <ExportButton 
                            onExport={handleExport}
                            formats={['PDF', 'CSV']}
                            disabled={!selectedType || (!selectedId && selectedType !== 'speciality' && selectedType !== 'institution' && selectedType !== 'rotation')}
                            customLabel="Export Showing Tables"
                        />
                    </div>
                </div>
            </div>

            <div className="vs-kpi-grid">
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{selectedType || 'N/A'}</p>
                    <p className="vs-kpi-label">Current schedule mode</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{selectedUsersCount}</p>
                    <p className="vs-kpi-label">Selected users</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{selectedCurriculumCount || loadedCurriculumCount}</p>
                    <p className="vs-kpi-label">Curriculums in scope</p>
                </div>
                <div className="vs-kpi-card">
                    <p className="vs-kpi-value">{visibleRowsCount}</p>
                    <p className="vs-kpi-label">Visible rows</p>
                </div>
            </div>

            <div className="view-admin-schedules-content" style={tableContainerStyle}>
                {error && (
                    <div className="notification error-notification" role="alert">
                        <span className="notification-icon">⚠️</span>
                        <div className="notification-content">{error}</div>
                    </div>
                )}
                
                {success && (
                    <div className="notification success-notification" role="alert">
                        <span className="notification-icon">✅</span>
                        <div className="notification-content">{success}</div>
                    </div>
                )}

                {/* Preceptor / Co-preceptor assign modal — rendered in portal so it stays viewport-centered while scrolling */}
                {preceptorAssignModal.open && ReactDOM.createPortal(
                    <div className="preceptor-assign-modal-overlay" onClick={closePreceptorAssignModal} role="dialog" aria-modal="true" aria-labelledby="preceptor-assign-modal-title">
                        <div className="preceptor-assign-modal" onClick={e => e.stopPropagation()}>
                            <div className="preceptor-assign-modal-header">
                                <h3 id="preceptor-assign-modal-title">
                                    {preceptorAssignModal.type === 'preceptor' ? 'Change Preceptor' : preceptorAssignModal.type === 'copreceptor' ? 'Add / Change Co-Preceptor' : 'Select'}
                                </h3>
                                <p className="preceptor-assign-modal-subtitle">
                                    {preceptorAssignModal.curriculumName} · Rotation {preceptorAssignModal.rotationNumber != null ? preceptorAssignModal.rotationNumber + 1 : ''}
                                </p>
                                <button type="button" className="preceptor-assign-modal-close" onClick={closePreceptorAssignModal} aria-label="Close">×</button>
                            </div>
                            <div className="preceptor-assign-modal-body">
                                {preceptorAssignModal.loading ? (
                                    <div className="preceptor-assign-modal-loading">
                                        <LoadingSpinner text="Loading preceptors..." variant="inline" size="small" />
                                    </div>
                                ) : preceptorAssignModal.list.length === 0 ? (
                                    <div className="preceptor-assign-modal-empty">
                                        No {preceptorAssignModal.type === 'copreceptor' ? 'co-' : ''}preceptors available for this curriculum. You can add availability for a preceptor below (Admin Preceptor Availability) or try another rotation.
                                    </div>
                                ) : (() => {
                                    const list = preceptorAssignModal.list;
                                    const search = (preceptorAssignModal.search || '').trim().toLowerCase();
                                    const filterSpecialityId = preceptorAssignModal.filterSpecialityId || '';
                                    const filterInstitutionId = preceptorAssignModal.filterInstitutionId || '';
                                    // Once the curriculum's matchBy is known (the student "Change Preceptor" flow,
                                    // which expands rows per entity), show only the ONE relevant filter — grouping
                                    // by experience for experience-matched curricula, by institution otherwise —
                                    // instead of two filters where only one can ever mean anything for a given row.
                                    const showSpecialityFilter = preceptorAssignModal.matchBy !== 'institution';
                                    const showInstitutionFilter = preceptorAssignModal.matchBy !== 'experience';
                                    const specialityOptions = [];
                                    const institutionOptions = [];
                                    const seenSpec = new Set();
                                    const seenInst = new Set();
                                    list.forEach(p => {
                                        const specId = p.specialityId || (p.speciality && (typeof p.speciality === 'object' ? p.speciality._id || p.speciality.name : String(p.speciality)));
                                        if (specId && !seenSpec.has(specId)) {
                                            seenSpec.add(specId);
                                            const label = (typeof p.speciality === 'object' && p.speciality ? (p.speciality.name || p.speciality.speciality) : null) || (typeof p.speciality === 'string' ? p.speciality : null) || specId;
                                            specialityOptions.push({ value: specId, label });
                                        }
                                        const instId = p.institutionId || (p.institution && (typeof p.institution === 'object' ? p.institution._id || p.institution.name : String(p.institution)));
                                        if (instId && !seenInst.has(instId)) {
                                            seenInst.add(instId);
                                            const label = (typeof p.institution === 'object' && p.institution ? p.institution.name : null) || (typeof p.institution === 'string' ? p.institution : null) || instId;
                                            institutionOptions.push({ value: instId, label });
                                        }
                                    });
                                    specialityOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));
                                    institutionOptions.sort((a, b) => String(a.label).localeCompare(String(b.label)));
                                    const filtered = list.filter(p => {
                                        const nameStr = [p.firstname, p.lastname].filter(Boolean).map(String).join(' ').toLowerCase();
                                        if (search && !nameStr.includes(search)) return false;
                                        if (filterSpecialityId) {
                                            const id = p.specialityId || (p.speciality && (typeof p.speciality === 'object' ? p.speciality._id || p.speciality.name : String(p.speciality)));
                                            if (id !== filterSpecialityId) return false;
                                        }
                                        if (filterInstitutionId) {
                                            const id = p.institutionId || (p.institution && (typeof p.institution === 'object' ? p.institution._id || p.institution.name : String(p.institution)));
                                            if (id !== filterInstitutionId) return false;
                                        }
                                        return true;
                                    });
                                    return (
                                    <>
                                    <div className="preceptor-assign-modal-filters">
                                        <input
                                            type="text"
                                            placeholder="Search by name..."
                                            value={preceptorAssignModal.search || ''}
                                            onChange={e => setPreceptorAssignModal(prev => ({ ...prev, search: e.target.value }))}
                                            className="preceptor-assign-filter-search"
                                            aria-label="Search preceptors by name"
                                        />
                                        {showSpecialityFilter && (
                                            <select
                                                value={preceptorAssignModal.filterSpecialityId || ''}
                                                onChange={e => setPreceptorAssignModal(prev => ({ ...prev, filterSpecialityId: e.target.value }))}
                                                className="preceptor-assign-filter-select"
                                                aria-label="Filter by experience"
                                            >
                                                <option value="">All experiences</option>
                                                {specialityOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}
                                        {showInstitutionFilter && (
                                            <select
                                                value={preceptorAssignModal.filterInstitutionId || ''}
                                                onChange={e => setPreceptorAssignModal(prev => ({ ...prev, filterInstitutionId: e.target.value }))}
                                                className="preceptor-assign-filter-select"
                                                aria-label="Filter by institution"
                                            >
                                                <option value="">All institutions</option>
                                                {institutionOptions.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    {filtered.length === 0 ? (
                                        <div className="preceptor-assign-modal-empty">No preceptors match the search or filters.</div>
                                    ) : (
                                    <ul className="preceptor-assign-list">
                                        {filtered.map((p) => {
                                            const { curriculumId, rotationNumber } = preceptorAssignModal;
                                            // Rows from the expanded student-preceptor flow carry a stable per-entity
                                            // rowKey; other flows (copreceptor, non-student) are one row per preceptor,
                                            // so their preceptor id already uniquely identifies the row.
                                            const rowKey = p.rowKey || p._id;
                                            const arr = curriculumId && p.rotationsAvailabilities && typeof p.rotationsAvailabilities === 'object'
                                                ? (p.rotationsAvailabilities[String(curriculumId)] ?? p.rotationsAvailabilities[curriculumId])
                                                : null;
                                            // `arr` is either a flat per-rotation array (legacy/other flows) or, for the
                                            // expanded student-preceptor rows, a dict of entityId -> array — read the
                                            // row's own entity slice in that case.
                                            const entityArr = p.entityId && arr && typeof arr === 'object' && !Array.isArray(arr)
                                                ? arr[p.entityId]
                                                : arr;
                                            const thisRotationVal = p.availability !== undefined
                                                ? p.availability
                                                : (Array.isArray(entityArr) && rotationNumber != null && rotationNumber >= 0 && rotationNumber < entityArr.length
                                                    ? entityArr[rotationNumber]
                                                    : 0);
                                            const status = getRotationAvailabilityLabel(thisRotationVal);
                                            const isEditingThis = preceptorAssignModal.editingAvailabilityFor === rowKey;
                                            const isSavingThis = preceptorAssignModal.savingAvailabilityFor === rowKey;
                                            const isUnavailable = thisRotationVal === 0;
                                            // For expanded rows (p.entityId set) with a known current-assigned entity,
                                            // only the ONE row matching both preceptor AND entity is "current" — not
                                            // every row for that preceptor. Other flows keep matching by preceptor alone.
                                            const isCurrentAssigned =
                                                preceptorAssignModal.currentAssignedPreceptorId != null &&
                                                String(preceptorAssignModal.currentAssignedPreceptorId) === String(p._id) &&
                                                (p.entityId === undefined || preceptorAssignModal.currentAssignedEntityId == null ||
                                                    String(p.entityId || '') === String(preceptorAssignModal.currentAssignedEntityId));
                                            const displayName = [p.firstname, p.lastname].filter(x => x != null && String(x) !== 'undefined').map(String).join(' ').trim() || '—';
                                            return (
                                                <li
                                                    key={rowKey}
                                                    className={`preceptor-assign-item ${isEditingThis ? 'is-editing' : ''} ${isUnavailable ? 'preceptor-assign-item--unavailable' : ''} ${isCurrentAssigned ? 'preceptor-assign-item--current' : ''}`}
                                                >
                                                    <div className="preceptor-assign-item-main">
                                                        <div className="preceptor-assign-item-info">
                                                            <span className="preceptor-assign-item-name">
                                                                {displayName}
                                                                {isCurrentAssigned && (
                                                                    <span className="preceptor-assign-current-pill">
                                                                        {preceptorAssignModal.type === 'copreceptor' ? 'Current Co-Preceptor' : 'Current Preceptor'}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <span className="preceptor-assign-item-meta">
                                                                {(typeof p.speciality === 'object' ? (p.speciality?.name || p.speciality?.speciality) : p.speciality) || ''}
                                                                {(typeof p.institution === 'object' ? p.institution?.name : p.institution) ? ` · ${typeof p.institution === 'object' ? p.institution.name : p.institution}` : ''}
                                                            </span>
                                                        </div>
                                                        <div className="preceptor-assign-item-availability">
                                                            <span className={`preceptor-assign-avail-badge ${status.class}`}>
                                                                {status.text}
                                                            </span>
                                                        </div>
                                                        <div className="preceptor-assign-item-actions">
                                                            <button
                                                                type="button"
                                                                className="preceptor-assign-btn preceptor-assign-btn-edit"
                                                                onClick={() => startEditingAvailability(rowKey)}
                                                                disabled={isSavingThis}
                                                            >
                                                                Edit availability
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="preceptor-assign-btn preceptor-assign-btn-select"
                                                                onClick={() => assignPreceptorToRow(p)}
                                                                disabled={!p._id || isUnavailable}
                                                                title={isUnavailable ? 'Update availability above to enable selection' : undefined}
                                                            >
                                                                Select
                                                            </button>
                                                            {isCurrentAssigned && (preceptorAssignModal.type === 'preceptor' || (selectedType === 'student' && preceptorAssignModal.type === 'copreceptor')) && (
                                                                <button
                                                                    type="button"
                                                                    className="preceptor-assign-btn preceptor-assign-btn-secondary"
                                                                    onClick={() => assignPreceptorToRow(null)}
                                                                >
                                                                    Unselect
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {isEditingThis && (() => {
                                                        const rn = preceptorAssignModal.rotationNumber != null ? preceptorAssignModal.rotationNumber : 0;
                                                        const draft = preceptorAssignModal.editAvailabilityDraft || Array(8).fill(0);
                                                        const singleVal = draft[rn];
                                                        const displayVal = singleVal !== undefined && singleVal !== null && singleVal !== ''
                                                            ? (typeof singleVal === 'number' ? singleVal : Math.max(0, Math.min(99, parseInt(singleVal, 10) || 0)))
                                                            : 0;
                                                        return (
                                                            <div className="preceptor-assign-edit-panel">
                                                                <p className="preceptor-assign-edit-label">This rotation only: {getCurriculumDisplayName(preceptorAssignModal.curriculumId)} · Rotation {rn + 1} (0 = off, or number of students)</p>
                                                                <div className="preceptor-assign-edit-grid">
                                                                    <div className="preceptor-assign-edit-cell">
                                                                        <label>Rotation {rn + 1}</label>
                                                                        <input
                                                                            type="number"
                                                                            min={0}
                                                                            max={99}
                                                                            value={displayVal}
                                                                            onChange={(e) => {
                                                                                const v = [...draft];
                                                                                v[rn] = e.target.value;
                                                                                setPreceptorAssignModal(prev => ({ ...prev, editAvailabilityDraft: v }));
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="preceptor-assign-edit-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="preceptor-assign-btn preceptor-assign-btn-secondary"
                                                                        onClick={() => setPreceptorAssignModal(prev => ({ ...prev, editingAvailabilityFor: null, editAvailabilityDraft: [], savingAvailabilityFor: null }))}
                                                                        disabled={isSavingThis}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="preceptor-assign-btn preceptor-assign-btn-primary"
                                                                        onClick={saveAvailabilityFromModal}
                                                                        disabled={isSavingThis}
                                                                    >
                                                                        {isSavingThis ? 'Saving...' : 'Save availability'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    )}
                                    </>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
                
                {/* Modern schedule controls: 1) Schedule type only at start, 2) Curriculum multi, 3) Entity selector, 4) Date range last */}
                <div className="modern-schedule-controls">
                    {/* Step 1: Schedule type — only show when not a dedicated page (no scheduleType prop) */}
                    {!scheduleType && (
                    <div className="control-group control-group--schedule-type">
                        <label className="control-label">Schedule Type</label>
                        <select
                            value={selectedType}
                            onChange={(e) => {
                                const newType = e.target.value;
                                setSelectedType(newType);
                                setSelectedId('');
                                setSelectedIds([]);
                                setSelectedCurriculums([]);
                                setCurrentYear('');
                                setSelectedYears([]);
                                setSchedule([]);
                                setDisplaySchedule([]);
                                setError('');
                                setStudentInfo(null);
                                setSpecialitySchedules({});
                                setAvailabilityInfo({});
                                setSelectedRotations([]);
                                setRotationSchedulesByNumber({});
                                setInstitutionScheduleData({});
                                setSpecialityScheduleData({});
                                setStartDate('');
                                setEndDate('');

                                if (newType) {
                                    if (curriculums.length === 0) fetchCurriculums();
                                    if (newType === 'student' && allData.students.length === 0) fetchStudents();
                                    if (newType === 'preceptor' && allData.preceptors.length === 0) fetchPreceptors();
                                    if ((newType === 'rotation' || newType === 'speciality' || newType === 'institution') && Object.keys(configurations).length === 0) fetchConfigurations();
                                }
                            }}
                            className="modern-select"
                            style={{
                                padding: '12px 16px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '10px',
                                fontSize: '0.95rem',
                                fontWeight: '500',
                                color: '#334155',
                                background: '#fff',
                                minHeight: '44px',
                                boxSizing: 'border-box',
                                width: '100%'
                            }}
                        >
                            <option value="">Select type</option>
                            <option value="student">Student</option>
                            <option value="preceptor">Preceptor</option>
                            <option value="speciality">Rotation/Experience</option>
                            <option value="institution">Institution</option>
                            <option value="rotation">Rotation</option>
                        </select>
                    </div>
                    )}

                    {/* Step 2: Curriculum selector */}
                    {selectedType && (
                        <div className="control-group control-group--curriculum">
                            <label className="control-label">📚 Curriculum</label>
                            <Select
                                isMulti={isCurriculumMultiSelect}
                                closeMenuOnSelect={!isCurriculumMultiSelect}
                                isLoading={loadingCurriculums}
                                value={curriculumSelectValue}
                                onChange={(selectedOptions) => {
                                    if (isCurriculumMultiSelect) {
                                        const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                        setSelectedCurriculums(values);
                                        setSelectedYears(values);
                                        if (values.length > 0) setCurrentYear(values[0]);
                                    } else {
                                        const selectedValue = selectedOptions?.value || '';
                                        setSelectedYears(selectedValue ? [selectedValue] : []);
                                        setCurrentYear(selectedValue || '');
                                        if (selectedType === 'rotation' && !selectedValue) {
                                            setSelectedRotations([]);
                                            setRotationSchedulesByNumber({});
                                        }
                                    }
                                }}
                                options={(curriculums || []).map(c => ({ value: c._id, label: c.name }))}
                                placeholder={loadingCurriculums ? 'Loading…' : ((curriculums || []).length === 0 ? 'No curriculums found' : 'Select curriculum...')}
                                isClearable
                                isDisabled={loadingCurriculums || (curriculums || []).length === 0}
                                noOptionsMessage={() => 'No curriculums available'}
                                styles={{
                                    container: base => ({ ...base, minWidth: 0, maxWidth: '100%' }),
                                    control: base => ({ ...base, minHeight: 44 })
                                }}
                            />
                        </div>
                    )}

                    {/* Step 3: Entity selector — students/preceptors or rotation numbers, affected by curriculum */}
                    {(selectedType === 'student' || selectedType === 'preceptor') && (
                        <div className="control-group control-group--multi-select">
                            <label className="control-label">
                                {selectedType === 'student' ? '🎓 Students' : '👨‍⚕️ Preceptors'}
                            </label>
                            <Select
                                isMulti
                                isLoading={(selectedType === 'student' && loadingStudents) || (selectedType === 'preceptor' && loadingPreceptors)}
                                value={selectedIds.map(id => {
                                    const data = selectedType === 'student' ? allData.students : allData.preceptors;
                                    const user = data.find(u => u._id === id);
                                    return user ? { value: user._id, label: `${user.firstname} ${user.lastname}`, email: user.email } : null;
                                }).filter(Boolean)}
                                onChange={(selectedOptions) => {
                                    const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                                    setSelectedIds(values);
                                    setSelectedId(values.length > 0 ? values[0] : '');
                                    setSchedulesByYear({});
                                    setYears([]);
                                    setSchedule([]);
                                    setDisplaySchedule([]);
                                    setMultiSchedules({});
                                }}
                                options={(() => {
                                    if (selectedType === 'student' && !loadingStudents) {
                                        const list = allData.students;
                                        const filtered = selectedCurriculums.length > 0
                                            ? list.filter(u => {
                                                const ids = getScheduleCurriculumIds(u, 'student');
                                                return ids.some(id => selectedCurriculums.includes(id) || selectedCurriculums.includes(String(id)));
                                            })
                                            : list;
                                        return filtered.sort((a, b) => (a.firstname || '').localeCompare(b.firstname || '') || (a.lastname || '').localeCompare(b.lastname || '')).map(u => ({ value: u._id, label: `${u.firstname || ''} ${u.lastname || ''}`.trim() || u._id, email: u.email }));
                                    }
                                    if (selectedType === 'preceptor' && !loadingPreceptors) {
                                        const list = allData.preceptors;
                                        const filtered = selectedCurriculums.length > 0
                                            ? list.filter(u => {
                                                const ids = getScheduleCurriculumIds(u, 'preceptor');
                                                return ids.some(id => selectedCurriculums.includes(id) || selectedCurriculums.includes(String(id)));
                                            })
                                            : list;
                                        return filtered.sort((a, b) => (a.firstname || '').localeCompare(b.firstname || '') || (a.lastname || '').localeCompare(b.lastname || '')).map(u => ({ value: u._id, label: `${u.firstname || ''} ${u.lastname || ''}`.trim() || u._id, email: u.email }));
                                    }
                                    return [];
                                })()}
                                placeholder={(selectedType === 'student' && loadingStudents) || (selectedType === 'preceptor' && loadingPreceptors) ? `Loading ${selectedType}s...` : `Select ${selectedType}(s)...`}
                                isDisabled={(selectedType === 'student' && loadingStudents) || (selectedType === 'preceptor' && loadingPreceptors)}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '48px', borderRadius: '12px', borderWidth: '2px', borderColor: '#e5e7eb', fontSize: '0.95rem', fontWeight: '500' }),
                                    multiValue: (base) => ({ ...base, backgroundColor: '#3b82f6', borderRadius: '8px' }),
                                    multiValueLabel: (base) => ({ ...base, color: 'white', fontWeight: '500' }),
                                    multiValueRemove: (base) => ({ ...base, color: 'white', ':hover': { backgroundColor: '#dc2626', color: 'white' } })
                                }}
                            />
                        </div>
                    )}

                    {/* Rotation: rotation numbers selector (options driven by curriculum) */}
                    {selectedType === 'rotation' && selectedYears.length > 0 && (
                        <div className="control-group control-group--curriculum">
                            <label className="control-label">Rotations</label>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                value={selectedRotations.map(r => ({ value: r, label: `Rotation ${r + 1}` }))}
                                onChange={(selected) => {
                                    const values = selected ? selected.map(opt => opt.value) : [];
                                    setSelectedRotations(values);
                                    if (values.length === 0) setRotationSchedulesByNumber({});
                                }}
                                options={Array.from({ length: 8 }, (_, i) => ({ value: i, label: `Rotation ${i + 1}` }))}
                                placeholder="Select rotation(s)..."
                                styles={{ container: base => ({ ...base, minWidth: 0, maxWidth: '100%' }), control: base => ({ ...base, minHeight: 40 }) }}
                            />
                        </div>
                    )}

                    {/* Step 4: Date range — compact, operates on what shows on screen */}
                    {selectedType && (
                        <div className="control-group control-group--date-range">
                            <label className="control-label">📅 Date range</label>
                            <div className="date-range-inner">
                                <div className="date-range-inputs">
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            if (!hasCurriculumForDateFilter) return;
                                            setStartDate(e.target.value);
                                            setShowCurrentOnly(false);
                                        }}
                                        aria-label="Start date"
                                        disabled={!hasCurriculumForDateFilter}
                                        title={!hasCurriculumForDateFilter ? 'Select curriculum first' : undefined}
                                    />
                                    <span className="date-range-arrow">→</span>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            if (!hasCurriculumForDateFilter) return;
                                            setEndDate(e.target.value);
                                            setShowCurrentOnly(false);
                                        }}
                                        min={startDate}
                                        aria-label="End date"
                                        disabled={!hasCurriculumForDateFilter}
                                        title={!hasCurriculumForDateFilter ? 'Select curriculum first' : undefined}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Clear all filters */}
                    <div className="control-group control-group--actions">
                        <button
                            type="button"
                            className="modern-button modern-button--clear-filters"
                            disabled={!hasAnyFilters}
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setSelectedYears([]);
                                setSelectedCurriculums([]);
                                setSelectedRotations([]);
                                setCurrentYear('');
                                setSchedulesByYear({});
                                setDisplaySchedule([]);
                                setSpecialitySchedules({});
                                setAvailabilityInfo({});
                                setRotationSchedulesByNumber({});
                                setInstitutionScheduleData({});
                                setSpecialityScheduleData({});
                                setShowCurrentOnly(false);
                            }}
                        >
                            🗑️ Clear all filters
                        </button>
                    </div>
                </div>

                {/* Loading state */}
                {(loadingStudents || loadingPreceptors || fetchingSchedule || dateRangeLoading || loadingCurriculums) && (
                    <LoadingSpinner 
                        text={
                            loadingStudents ? 'Loading students...' :
                            loadingPreceptors ? 'Loading preceptors...' :
                            loadingCurriculums ? 'Loading curriculums...' :
                            fetchingSchedule ? 'Loading schedule data...' :
                            dateRangeLoading ? 'Loading date range data...' :
                            'Loading...'
                        } 
                    />
                )}

                {/* Content area */}
                {!(loadingStudents || loadingPreceptors || fetchingSchedule || dateRangeLoading) && (
                    <div className="schedule-results">
                        {/* No selection message */}
                        {!selectedType && (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '3rem', 
                                    textAlign: 'center', 
                                    background: '#f8fafc', 
                                    border: '2px dashed #cbd5e1', 
                                    borderRadius: '12px',
                                    color: '#475569'
                                }}>
                                    <span style={{ fontSize: '4rem', marginBottom: '1rem', display: 'block' }}>📊</span>
                                    <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
                                        Welcome to Admin Schedules
                                    </h2>
                                    <p style={{ margin: 0, fontSize: '1.1rem' }}>
                                        Select a schedule type from the dropdown above to begin viewing and managing schedules.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Student/Preceptor selection message */}
                        {(selectedType === 'student' || selectedType === 'preceptor') && !selectedId && (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#f0f9ff', 
                                    border: '2px dashed #60a5fa', 
                                    borderRadius: '12px',
                                    color: '#1e40af'
                                }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>
                                        {selectedType === 'student' ? '👨‍🎓' : '👨‍⚕️'}
                                    </span>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                        Select a {selectedType === 'student' ? 'Student' : 'Preceptor'}
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>
                                        Please select a {selectedType} from the dropdown above to view their schedule.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Multi-User Schedule Display */}
                        {(selectedType === 'student' || selectedType === 'preceptor') && selectedIds.length > 1 && Object.keys(multiSchedules).length > 0 && (
                            <>
                                <div style={{ 
                                    marginBottom: '1.5rem', 
                                    padding: '1.5rem', 
                                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)', 
                                    borderRadius: '16px', 
                                    color: '#fff',
                                    textAlign: 'center',
                                    boxShadow: '0 10px 30px rgba(43, 70, 99, 0.3)'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700' }}>
                                        {selectedType === 'student' ? '👥 Multi-Student Schedule Comparison' : '👥 Multi-Preceptor Schedule Comparison'}
                                    </h3>
                                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '1rem', opacity: 0.9 }}>
                                        Showing {selectedIds.length} {selectedType}s 
                                        {selectedCurriculums.length > 0 ? 
                                            ` across ${selectedCurriculums.length} curriculum${selectedCurriculums.length > 1 ? 's' : ''}` :
                                            ' (all available curriculums)'
                                        }
                                    </p>
                                    <div style={{ 
                                        marginTop: '1rem', 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: '0.5rem', 
                                        justifyContent: 'center' 
                                    }}>
                                        {selectedCurriculums.length > 0 ? 
                                            selectedCurriculums.map(currId => (
                                                <span key={currId} style={{
                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '20px',
                                                    fontSize: '0.85rem',
                                                    backdropFilter: 'blur(10px)'
                                                }}>
                                                    📚 {getCurriculumDisplayName(currId)}
                                                </span>
                                            )) :
                                            <span style={{
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '20px',
                                                fontSize: '0.85rem',
                                                backdropFilter: 'blur(10px)',
                                                fontStyle: 'italic'
                                            }}>
                                                📚 All Available Curriculums
                                            </span>
                                        }
                                    </div>
                                </div>

                                {/* Multi-User Schedule Summary Cards */}
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
                                    gap: '1.5rem', 
                                    marginBottom: '2rem' 
                                }}>
                                    {Object.entries(multiSchedules).map(([userId, userSchedule]) => (
                                        <div key={userId} style={{
                                            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                            border: '2px solid #0ea5e9',
                                            borderRadius: '16px',
                                            padding: '1.5rem',
                                            boxShadow: '0 4px 15px rgba(14, 165, 233, 0.1)'
                                        }}>
                                            <h4 style={{ 
                                                margin: '0 0 1rem 0', 
                                                color: '#0c4a6e', 
                                                fontSize: '1.1rem',
                                                fontWeight: '700'
                                            }}>
                                                {selectedType === 'student' ? '🎓' : '👨‍⚕️'} {userSchedule.name}
                                            </h4>
                                            <p style={{ 
                                                margin: '0 0 1rem 0', 
                                                color: '#64748b', 
                                                fontSize: '0.9rem' 
                                            }}>
                                                {userSchedule.email}
                                            </p>
                                            
                                            {userSchedule.error ? (
                                                <div style={{
                                                    padding: '1rem',
                                                    background: '#fef2f2',
                                                    border: '1px solid #fecaca',
                                                    borderRadius: '8px',
                                                    color: '#dc2626',
                                                    fontSize: '0.9rem'
                                                }}>
                                                    ⚠️ {userSchedule.error}
                                                </div>
                                            ) : (
                                                <div>
                                                    {(selectedCurriculums.length > 0 ? selectedCurriculums : Object.keys(userSchedule.data)).map(currId => {
                                                        const rotationsCount = userSchedule.data[currId] ? userSchedule.data[currId].length : 0;
                                                        return (
                                                            <div key={currId} style={{
                                                                padding: '0.75rem',
                                                                background: rotationsCount > 0 ? '#dcfce7' : '#fef3c7',
                                                                border: `1px solid ${rotationsCount > 0 ? '#bbf7d0' : '#fde68a'}`,
                                                                borderRadius: '8px',
                                                                marginBottom: '0.5rem'
                                                            }}>
                                                                <div style={{ 
                                                                    fontWeight: '600', 
                                                                    color: rotationsCount > 0 ? '#166534' : '#92400e',
                                                                    fontSize: '0.9rem',
                                                                    marginBottom: '0.25rem'
                                                                }}>
                                                                    📚 {getCurriculumDisplayName(currId)}
                                                                </div>
                                                                <div style={{ 
                                                                    color: rotationsCount > 0 ? '#166534' : '#92400e',
                                                                    fontSize: '0.85rem' 
                                                                }}>
                                                                    {rotationsCount > 0 ? 
                                                                        `✅ ${rotationsCount} rotation${rotationsCount > 1 ? 's' : ''}` : 
                                                                        '❌ No rotations assigned'
                                                                    }
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Combined Multi-User Schedule Table */}
                                {displayScheduleFiltered.length > 0 && (
                                    <div>
                                        <div style={{
                                            background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                                            color: '#fff',
                                            padding: '1rem 1.5rem',
                                            borderRadius: '12px 12px 0 0',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>
                                                📊 Combined Schedule Overview ({displayScheduleFiltered.length} total rotations)
                                            </h4>
                                            <ColumnSelector
                                                availableColumns={['userName', 'curriculumName', ...availableColumns[selectedType]]}
                                                visibleColumns={['userName', 'curriculumName', ...visibleColumns[selectedType]]}
                                                onColumnToggle={(column) => {
                                                    if (column === 'userName' || column === 'curriculumName') {
                                                        // Special handling for multi-user columns
                                                        const currentVisible = visibleColumns[selectedType];
                                                        const newVisible = currentVisible.includes(column) 
                                                            ? currentVisible.filter(col => col !== column)
                                                            : [...currentVisible, column];
                                                        setVisibleColumns(prev => ({ ...prev, [selectedType]: newVisible }));
                                                    } else {
                                                        handleColumnToggle(column);
                                                    }
                                                }}
                                                onExportTable={() => handleExport('CSV')}
                                            />
                                        </div>
                                        {renderScheduleTable(displayScheduleFiltered, selectedType, true)}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Single Student Schedule Display */}
                        {selectedType === 'student' && selectedId && selectedIds.length <= 1 && displayScheduleFiltered.length > 0 && (
                            <>
                                <div style={{ 
                                    marginBottom: '1.5rem', 
                                    padding: '1rem', 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px', 
                                    color: '#495057',
                                    textAlign: 'center',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
                                        👨‍🎓 Student Schedule - {(() => {
                                            const s = allData.students.find(s => s._id === selectedId);
                                            return s ? `${s.firstname} ${s.lastname}` : 'Selected Student';
                                        })()}
                                    </h3>
                                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                        Showing curriculum: {getCurriculumDisplayName(selectedYears[0] || currentYear || new Date().getFullYear())}
                                    </p>
                                </div>
                                {renderScheduleTable(displayScheduleFiltered, 'student')}
                            </>
                        )}

                        {/* Preceptor Schedule Display */}
                        {selectedType === 'preceptor' && selectedId && displayScheduleFiltered.length > 0 && (
                            <>
                                <div style={{ 
                                    marginBottom: '1.5rem', 
                                    padding: '1rem', 
                                    background: '#f8f9fa', 
                                    borderRadius: '8px', 
                                    color: '#495057',
                                    textAlign: 'center',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700' }}>
                                        👨‍⚕️ Preceptor Schedule - {(() => {
                                            const p = allData.preceptors.find(p => p._id === selectedId);
                                            return p ? `${p.firstname} ${p.lastname}` : 'Selected Preceptor';
                                        })()}
                                    </h3>
                                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
                                        Showing curriculum: {getCurriculumDisplayName(selectedYears[0] || currentYear || new Date().getFullYear())}
                                    </p>
                                </div>
                                {renderScheduleTable(displayScheduleFiltered, 'preceptor')}
                            </>
                        )}

                        {/* No schedule data message */}
                        {selectedId && !isEditing && !fetchingSchedule && (selectedYears.length > 0 || currentYear) && (
                            (selectedType === 'student' && (!displayScheduleFiltered || displayScheduleFiltered.length === 0)) ||
                            (selectedType === 'preceptor' && (!displayScheduleFiltered || displayScheduleFiltered.length === 0))
                        ) && (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#f8fafc', 
                                    border: '2px dashed #cbd5e1', 
                                    borderRadius: '12px',
                                    color: '#475569'
                                }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📋</span>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                        No Schedule Data Available
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>
                                        {selectedType === 'student' ? 
                                            'This student does not have a schedule assigned for the selected curriculum yet.' :
                                            'This preceptor does not have any students assigned yet. Please check the schedule or contact support.'
                                        }
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Curriculum selection message for student/preceptor — hide when schedule is already showing */}
                        {(selectedType === 'student' || selectedType === 'preceptor') && selectedId && curriculums.length > 0 && selectedCurriculums.length === 0 && (!displayScheduleFiltered || displayScheduleFiltered.length === 0) && (
                            <div className="no-schedule-message">
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#f0f9ff', 
                                    border: '2px dashed #60a5fa', 
                                    borderRadius: '12px',
                                    color: '#1e40af'
                                }}>
                                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📚</span>
                                    <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.3rem', fontWeight: '700' }}>
                                        Select a Curriculum
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '1rem' }}>
                                        Please select one or more curricula above to view the {selectedType}'s schedule.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Rotation mode no selection message */}
                        {selectedType === 'rotation' && selectedYears.length === 0 && (
                            <div className="no-schedule-message">
                                <div className="rotation-guidance-card">
                                    <h3 className="rotation-guidance-title">
                                        Select Curriculum and Rotation(s)
                                    </h3>
                                    <p className="rotation-guidance-text">
                                        Select one or more curricula above, then choose rotation(s) to view schedule data.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Rotation mode with selection message */}
                        {selectedType === 'rotation' && selectedYears.length > 0 && selectedRotations.length === 0 && (
                            <div className="no-schedule-message">
                                <div className="rotation-guidance-card rotation-guidance-card--warning">
                                    <h3 className="rotation-guidance-title">
                                        Select Rotation(s) to View
                                    </h3>
                                    <p className="rotation-guidance-text">
                                        Choose one or more rotations from the dropdown above to view detailed schedule information for {getCurriculumDisplayName(currentYear)}.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Rotation Mode Schedules */}
                        {selectedType === 'rotation' && currentYear && selectedRotations.length > 0 && (
                            <div className="rotation-mode-container">
                                {/* Loading spinner for rotation mode */}
                                {fetchingSchedule && (
                                    <LoadingSpinner text="Loading rotation schedule data..." />
                                )}

                                {/* Render each selected rotation */}
                                {selectedRotations.map((rotationNumber) => {
                                    const rotationData = rotationSchedulesByNumber[rotationNumber];
                                    
                                    if (!rotationData && !fetchingSchedule) {
                                        return (
                                            <div key={rotationNumber} className="rotation-section">
                                                <div className="rotation-header">
                                                    <h3>Rotation {rotationNumber + 1}</h3>
                                                    <div className="rotation-stats">
                                                        <span className="stat">No Data Available</span>
                                                    </div>
                                                </div>
                                                <div className="rotation-no-data-state">
                                                    <h4 className="rotation-no-data-title">
                                                        No Schedule Data Found
                                                    </h4>
                                                    <p className="rotation-no-data-text">
                                                        No schedule data found for Rotation {rotationNumber + 1} in {getCurriculumDisplayName(currentYear)}.
                                                    </p>
                                                    <p className="rotation-no-data-text rotation-no-data-text--muted">
                                                        Please check if students and preceptors have been assigned to this rotation.
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (!rotationData) return null;

                                    const students = normalizeRotationStudents(rotationData.students || [], rotationData.preceptors || []);
                                    const preceptors = normalizeRotationPreceptors(rotationData.preceptors || [], students);

                                    return (
                                        <div key={rotationNumber} className="rotation-section">
                                            <div className="rotation-header">
                                                <div>
                                                    <h3>Rotation {rotationNumber + 1}</h3>
                                                    <p className="rotation-subtitle">
                                                        {getCurriculumDisplayName(currentYear)} • Academic Schedule
                                                    </p>
                                                </div>
                                                <div className="rotation-stats">
                                                    <span className="stat">{students.length} Students</span>
                                                    <span className="stat">{preceptors.length} Preceptors</span>
                                                    <span className="stat">
                                                        {students.length > 0 && preceptors.length > 0
                                                            ? `${(students.length / preceptors.length).toFixed(1)}:1 Ratio`
                                                            : 'No Ratio'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Students Section */}
                                            <div className="rotation-subsection">
                                                <div className="subsection-header rotation-subsection-header">
                                                    <div>
                                                        <h4 className="rotation-section-title">
                                                            Students in Rotation {rotationNumber + 1}
                                                        </h4>
                                                        <p className="rotation-section-count">
                                                            {students.length} student{students.length !== 1 ? 's' : ''} assigned
                                                        </p>
                                                    </div>
                                                    <div className="rotation-header-actions">
                                                        <ColumnSelector
                                                            availableColumns={availableColumns.rotationStudents}
                                                            visibleColumns={visibleColumns.rotationStudents}
                                                            onColumnToggle={handleRotationStudentsColumnToggle}
                                                            onExportTable={() => exportRotationStudentsTable(students, rotationNumber)}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {students.length === 0 ? (
                                                    <div className="rotation-empty-state">
                                                        <p className="rotation-empty-title">No students are assigned to this rotation.</p>
                                                        <p className="rotation-empty-text">Student rows will appear once assignments are available for Rotation {rotationNumber + 1}.</p>
                                                    </div>
                                                ) : (
                                                    <div className="modern-schedule-table-container">
                                                        <table className="modern-schedule-table">
                                                            <thead>
                                                                <tr>
                                                                    {visibleColumns.rotationStudents.includes('name') && (
                                                                        <SortableTableHeader 
                                                                            label="Student Name" 
                                                                            columnKey="name" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('email') && (
                                                                        <SortableTableHeader 
                                                                            label="Email" 
                                                                            columnKey="email" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('rank') && (
                                                                        <SortableTableHeader 
                                                                            label="Rank" 
                                                                            columnKey="rank" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('phone') && (
                                                                        <SortableTableHeader 
                                                                            label="Phone" 
                                                                            columnKey="phone" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('speciality') && (
                                                                        <SortableTableHeader 
                                                                            label="Experience" 
                                                                            columnKey="specialty_name" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('institution') && (
                                                                        <SortableTableHeader 
                                                                            label="Institution" 
                                                                            columnKey="institution_name" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationStudents.includes('preceptor_name') && (
                                                                        <SortableTableHeader 
                                                                            label="Preceptor" 
                                                                            columnKey="preceptor_name" 
                                                                            sortState={sortState.rotationStudents} 
                                                                            onSort={(col) => handleSort('rotationStudents', col)} 
                                                                        />
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {getSortedData(students, 'rotationStudents').map((student, index) => (
                                                                    <tr key={student._rowId} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                                                        {visibleColumns.rotationStudents.includes('name') && (
                                                                            <td className="rotation-primary-cell">
                                                                                {student.name}
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('email') && (
                                                                            <td>{student.email || 'Not provided'}</td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('rank') && (
                                                                            <td>
                                                                                <span className="rotation-badge">
                                                                                    {student.rank || 'Not specified'}
                                                                                </span>
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('phone') && (
                                                                            <td>{student.phone || 'Not provided'}</td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('speciality') && (
                                                                            <td>
                                                                                {student.specialty_name ? (
                                                                                    <span className="specialty-badge-rotation">
                                                                                        {toDisplayText(student.specialty_name) || 'Not assigned'}
                                                                                    </span>
                                                                                ) : 'Not assigned'}
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('institution') && (
                                                                            <td>
                                                                                {student.institution_name ? (
                                                                                    <span className="institution-badge-rotation">
                                                                                        {toDisplayText(student.institution_name) || 'Not assigned'}
                                                                                    </span>
                                                                                ) : 'Not assigned'}
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationStudents.includes('preceptor_name') && (
                                                                            <td>
                                                                                {student.preceptor_name ? (
                                                                                    <span className="preceptor-badge-rotation">
                                                                                        {toDisplayText(student.preceptor_name) || 'Not assigned'}
                                                                                    </span>
                                                                                ) : 'Not assigned'}
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Preceptors Section */}
                                            <div className="rotation-subsection">
                                                <div className="subsection-header rotation-subsection-header">
                                                    <div>
                                                        <h4 className="rotation-section-title">
                                                            Preceptors in Rotation {rotationNumber + 1}
                                                        </h4>
                                                        <p className="rotation-section-count">
                                                            {preceptors.length} preceptor{preceptors.length !== 1 ? 's' : ''} assigned
                                                        </p>
                                                    </div>
                                                    <div className="rotation-header-actions">
                                                        <ColumnSelector
                                                            availableColumns={availableColumns.rotationPreceptors}
                                                            visibleColumns={visibleColumns.rotationPreceptors}
                                                            onColumnToggle={handleRotationPreceptorsColumnToggle}
                                                            onExportTable={() => exportRotationPreceptorsTable(preceptors, rotationNumber)}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                {preceptors.length === 0 ? (
                                                    <div className="rotation-empty-state">
                                                        <p className="rotation-empty-title">No preceptors are assigned to this rotation.</p>
                                                        <p className="rotation-empty-text">Preceptor rows will appear once assignments are available for Rotation {rotationNumber + 1}.</p>
                                                    </div>
                                                ) : (
                                                    <div className="modern-schedule-table-container">
                                                        <table className="modern-schedule-table">
                                                            <thead>
                                                                <tr>
                                                                    {visibleColumns.rotationPreceptors.includes('name') && (
                                                                        <SortableTableHeader 
                                                                            label="Preceptor Name" 
                                                                            columnKey="name" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationPreceptors.includes('email') && (
                                                                        <SortableTableHeader 
                                                                            label="Email" 
                                                                            columnKey="email" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationPreceptors.includes('rank') && (
                                                                        <SortableTableHeader 
                                                                            label="Rank" 
                                                                            columnKey="rank" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationPreceptors.includes('phone') && (
                                                                        <SortableTableHeader 
                                                                            label="Phone" 
                                                                            columnKey="phone" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationPreceptors.includes('students_count') && (
                                                                        <SortableTableHeader 
                                                                            label="Students Assigned" 
                                                                            columnKey="students_count" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                    {visibleColumns.rotationPreceptors.includes('student_details') && (
                                                                        <SortableTableHeader 
                                                                            label="Student Details" 
                                                                            columnKey="student_details" 
                                                                            sortState={sortState.rotationPreceptors} 
                                                                            onSort={(col) => handleSort('rotationPreceptors', col)} 
                                                                        />
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {getSortedData(preceptors, 'rotationPreceptors').map((preceptor, index) => (
                                                                    <tr key={preceptor._rowId} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                                                        {visibleColumns.rotationPreceptors.includes('name') && (
                                                                            <td className="rotation-primary-cell">
                                                                                {toDisplayText(preceptor.name) || toDisplayText(preceptor) || 'Unknown'}
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationPreceptors.includes('email') && (
                                                                            <td>{preceptor.email || 'Not provided'}</td>
                                                                        )}
                                                                        {visibleColumns.rotationPreceptors.includes('rank') && (
                                                                            <td>
                                                                                <span className="rotation-badge">
                                                                                    {preceptor.rank || 'Not specified'}
                                                                                </span>
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationPreceptors.includes('phone') && (
                                                                            <td>{preceptor.phone || 'Not provided'}</td>
                                                                        )}
                                                                        {visibleColumns.rotationPreceptors.includes('students_count') && (
                                                                            <td>
                                                                                <span className="students-count-badge">
                                                                                    {preceptor.students_count || 0} Students
                                                                                </span>
                                                                            </td>
                                                                        )}
                                                                        {visibleColumns.rotationPreceptors.includes('student_details') && (
                                                                            <td>
                                                                                {preceptor.students_data && preceptor.students_data.length > 0 ? (
                                                                                    <div className="rotation-student-detail-list">
                                                                                        {preceptor.students_data.slice(0, 3).map((student, studentIndex) => (
                                                                                            <span
                                                                                                key={`${student.studentId || 'student'}-${studentIndex}`}
                                                                                                className="student-detail-tag"
                                                                                            >
                                                                                                {student.name}
                                                                                            </span>
                                                                                        ))}
                                                                                        {preceptor.students_data.length > 3 && (
                                                                                            <span className="rotation-more-students-text">
                                                                                                +{preceptor.students_data.length - 3} more...
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <span className="rotation-empty-inline">
                                                                                        No students
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Institution Mode Schedules */}
                        {selectedType === 'institution' && (
                            <div className="institution-mode-container">
                                {/* Loading spinner */}
                                {fetchingSchedule && (
                                    <LoadingSpinner text="Loading institution schedule data..." />
                                )}

                                {/* Student Selector Dropdown */}
                                {selectedYears.length > 0 && (
                                    <div style={{
                                        background: '#fff',
                                        border: '2px solid #f3f4f6',
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '2rem',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
                                    }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            color: '#2b4663',
                                            marginBottom: '0.75rem'
                                        }}>
                                            🔍 Highlight Student Assignments
                                        </label>
                                        <Select
                                            value={selectedStudent ? { value: selectedStudent, label: selectedStudentName || selectedStudent } : null}
                                            onChange={(option) => handleSelectedStudentChange(option?.value || null)}
                                            options={availableStudents.map((student) => ({ value: student.id, label: student.name }))}
                                            placeholder={availableStudents.length > 0 ? 'Type to search students...' : 'No students found for selected curriculum(s)'}
                                            isSearchable
                                            isClearable
                                            noOptionsMessage={() => 'No matching students'}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    minHeight: '46px',
                                                    borderRadius: '8px',
                                                    borderWidth: '2px',
                                                    borderColor: state.isFocused ? '#0ea5e9' : '#d1d5db',
                                                    boxShadow: state.isFocused ? '0 0 0 3px rgba(14, 165, 233, 0.1)' : 'none',
                                                    '&:hover': { borderColor: state.isFocused ? '#0ea5e9' : '#d1d5db' }
                                                }),
                                                placeholder: (base) => ({ ...base, color: '#6b7280' }),
                                                singleValue: (base) => ({ ...base, color: '#2b4663' }),
                                                menu: (base) => ({ ...base, zIndex: 20 }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isFocused ? '#e0f2fe' : '#fff',
                                                    color: '#1f2937'
                                                })
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Render institution data */}
                                {Object.entries(institutionScheduleData).map(([curriculumKey, curriculumData]) => (
                                    <AvailabilityMatrixTable
                                        key={curriculumKey}
                                        scheduleData={curriculumData}
                                        type="institution"
                                        curriculumId={curriculumKey}
                                        curriculumName={getCurriculumDisplayName(curriculumKey)}
                                        selectedStudent={selectedStudent}
                                        selectedStudentId={selectedStudentId}
                                        selectedStudentName={selectedStudentName}
                                        onDirtyChange={handleMatrixDirtyChange}
                                    />
                                ))}

                                {/* Empty state for institution */}
                                {Object.keys(institutionScheduleData).length === 0 && !fetchingSchedule && (
                                    <div className="institution-no-data-state">
                                        <span className="no-data-icon">🏢</span>
                                        <h3>No Institution Data Available</h3>
                                        <p>Please select a curriculum year to view institution schedules.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Speciality/Rotation Experience Mode Schedules */}
                        {selectedType === 'speciality' && (
                            <div className="speciality-mode-container">
                                {/* Loading spinner */}
                                {fetchingSchedule && (
                                    <LoadingSpinner text="Loading experience schedule data..." />
                                )}

                                {/* Student Selector Dropdown */}
                                {selectedYears.length > 0 && (
                                    <div style={{
                                        background: '#fff',
                                        border: '2px solid #f3f4f6',
                                        borderRadius: '12px',
                                        padding: '1.5rem',
                                        marginBottom: '2rem',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
                                    }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            color: '#2b4663',
                                            marginBottom: '0.75rem'
                                        }}>
                                            🔍 Highlight Student Assignments
                                        </label>
                                        <Select
                                            value={selectedStudent ? { value: selectedStudent, label: selectedStudentName || selectedStudent } : null}
                                            onChange={(option) => handleSelectedStudentChange(option?.value || null)}
                                            options={availableStudents.map((student) => ({ value: student.id, label: student.name }))}
                                            placeholder={availableStudents.length > 0 ? 'Type to search students...' : 'No students found for selected curriculum(s)'}
                                            isSearchable
                                            isClearable
                                            noOptionsMessage={() => 'No matching students'}
                                            styles={{
                                                control: (base, state) => ({
                                                    ...base,
                                                    minHeight: '46px',
                                                    borderRadius: '8px',
                                                    borderWidth: '2px',
                                                    borderColor: state.isFocused ? '#0ea5e9' : '#d1d5db',
                                                    boxShadow: state.isFocused ? '0 0 0 3px rgba(14, 165, 233, 0.1)' : 'none',
                                                    '&:hover': { borderColor: state.isFocused ? '#0ea5e9' : '#d1d5db' }
                                                }),
                                                placeholder: (base) => ({ ...base, color: '#6b7280' }),
                                                singleValue: (base) => ({ ...base, color: '#2b4663' }),
                                                menu: (base) => ({ ...base, zIndex: 20 }),
                                                option: (base, state) => ({
                                                    ...base,
                                                    backgroundColor: state.isFocused ? '#e0f2fe' : '#fff',
                                                    color: '#1f2937'
                                                })
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Render speciality data */}
                                {Object.entries(specialityScheduleData).map(([curriculumKey, curriculumData]) => (
                                    <AvailabilityMatrixTable
                                        key={curriculumKey}
                                        scheduleData={curriculumData}
                                        type="experience"
                                        curriculumId={curriculumKey}
                                        curriculumName={getCurriculumDisplayName(curriculumKey)}
                                        selectedStudent={selectedStudent}
                                        selectedStudentId={selectedStudentId}
                                        selectedStudentName={selectedStudentName}
                                        onDirtyChange={handleMatrixDirtyChange}
                                    />
                                ))}

                                {/* Empty state for speciality */}
                                {Object.keys(specialityScheduleData).length === 0 && !fetchingSchedule && (
                                    <div className="speciality-no-data-state">
                                        <span className="no-data-icon">🏥</span>
                                        <h3>No Rotation Type Data Available</h3>
                                        <p>Please select a curriculum year to view experience schedules.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewAdminSchedules;
